import { useState, useRef, useEffect, useCallback, useMemo } from "react";
import {
  Send,
  Sparkles,
  Bot,
  User,
  AlertCircle,
  Maximize2,
  Minimize2,
  Expand,
  Shrink,
  PanelRightClose,
  FileText,
  Wand2,
  Layout,
  Sliders,
  Film,
  RotateCcw,
  Cpu,
  Square,
  Loader2,
  CheckCircle2,
  HelpCircle,
  Coins,
  Copy,
  Check,
  ChevronDown,
  ChevronUp,
  Eye,
  EyeOff,
  X,
  AlertTriangle,
  Info,
  ArrowRight,
  ExternalLink,
} from "lucide-react";
import { useStudio } from "@/store/StudioContext";
import { useAuth } from "@/store/AuthContext";
import { DemoBadge } from "@/components/common/States";
import MarkdownContent from "@/components/common/MarkdownContent";
import InformativeContinuityModal from "@/components/project/InformativeContinuityModal";
import { ScriptSelectionModal } from "@/components/project/ScriptSelectionModal";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { t } from "@/i18n";
import { api } from "@/lib/api";
import { streamAssistantChat } from "@/lib/sseClient";
import {
  analyzeProjectContinuity,
  CONTINUITY_RISK_TYPES,
  RISK_TYPE_CONFIG,
  isObservationFixable,
} from "@/domain/continuity";
import {
  buildProductionPlanPrompt,
  extractPlansFromContent,
  computePlanSnapshot,
  checkPlanValidity,
  isPlanCorrectionRequest,
  findLatestPlanInMessages,
  buildPlanCorrectionPrompt,
  getCanonicalPlanDisplay,
} from "@/domain/productionPlan";
import ProductionPlanCard from "@/components/project/ProductionPlanCard";
import ProductionPlanModal from "@/components/project/ProductionPlanModal";
import PlanRequestModal from "@/components/project/PlanRequestModal";

const QUICK_ACTIONS = [
  {
    key: "script",
    icon: FileText,
    label: () => t.assistant.quickActions.script.label,
    prompt: () => t.assistant.quickActions.script.prompt,
  },
  {
    key: "prompt",
    icon: Wand2,
    label: () => t.assistant.quickActions.prompt.label,
    prompt: () => t.assistant.quickActions.prompt.prompt,
  },
  {
    key: "improve",
    icon: Sparkles,
    label: () => t.assistant.quickActions.improve.label,
    prompt: () => t.assistant.quickActions.improve.prompt,
  },
  {
    key: "storyboard",
    icon: Layout,
    label: () => t.assistant.quickActions.storyboard.label,
    prompt: () => t.assistant.quickActions.storyboard.prompt,
  },
  {
    key: "continuity",
    icon: Sliders,
    label: () => t.assistant.quickActions.continuity.label,
    prompt: () => t.assistant.quickActions.continuity.prompt,
  },
  {
    key: "plan",
    icon: Film,
    label: () => t.assistant.quickActions.plan.label,
    prompt: () => t.assistant.quickActions.plan.prompt,
  },
];

export const extractProposalsFromContent = (content) => {
  if (!content || typeof content !== "string") return [];
  const matches = [...content.matchAll(/<veyra_proposal>\s*(\{[\s\S]*?\})\s*<\/veyra_proposal>/g)];
  const extracted = [];
  for (const match of matches) {
    try {
      const parsed = JSON.parse(match[1]);
      if (parsed && typeof parsed === "object") {
        extracted.push(parsed);
      }
    } catch {
      // Ignorar bloques JSON incompletos o malformados
    }
  }
  return extracted;
};

const PromptUpdateProposalCard = ({ proposal, projectId, data: propsData, messageId }) => {
  const studio = useStudio();
  const dispatch = studio?.dispatch;
  const data = propsData || studio?.data;
  const [isConfirming, setIsConfirming] = useState(false);
  const [isApplying, setIsApplying] = useState(false);

  // Toma destino en el estado actual del proyecto
  const targetScene = useMemo(() => {
    return (data?.scenes || []).find(
      (s) => s.id === proposal.sceneId && (!proposal.projectId || !s.projectId || s.projectId === proposal.projectId)
    );
  }, [data?.scenes, proposal.sceneId, proposal.projectId]);

  // Proyecto ajeno o toma eliminada
  const isUnauthorizedProject = Boolean(proposal.projectId && projectId && proposal.projectId !== projectId);
  const isMissingScene = !targetScene || isUnauthorizedProject;

  // Título legible de destino
  const destinationTitle = targetScene?.title?.trim() || proposal.targetSceneTitle || (targetScene?.order !== undefined ? `Toma ${targetScene.order + 1}` : proposal.sceneId);

  // Toma actualmente seleccionada en Storyboard
  const currentSelectedSceneId = data?.selection?.[projectId]?.sceneId || data?.selection?.sceneId;
  const isSelectionMismatch = Boolean(currentSelectedSceneId && currentSelectedSceneId !== proposal.sceneId);

  // Limpieza defensiva del prompt propuesto (retira prefijos como @Escena N [Xs])
  const cleanProposedPrompt = useMemo(() => {
    return (proposal.proposedPrompt || "").replace(/^\s*@Escena\s+\d+\s*(?:\[\d+s\])?\s*[\r\n]*/i, "").trim();
  }, [proposal.proposedPrompt]);

  // Duración sugerida consultiva
  const suggestedDuration = proposal.suggestedDuration;

  // Detección de prompt modificado durante la espera
  const currentPromptInScene = targetScene?.prompt || "";
  const isPromptModified = Boolean(
    targetScene &&
    proposal.originalPrompt !== undefined &&
    proposal.originalPrompt !== null &&
    currentPromptInScene !== proposal.originalPrompt
  );

  const isApplied = Boolean(proposal.applied);
  const isDismissed = Boolean(proposal.dismissed);

  const handleApplyClick = () => {
    if (isMissingScene || isApplied || isDismissed) return;
    setIsConfirming(true);
  };

  const handleCancelClick = () => {
    if (isConfirming) {
      setIsConfirming(false);
      return;
    }
    dispatch({
      type: "PROPOSAL_DISMISSED",
      payload: { projectId: proposal.projectId || projectId, proposalId: proposal.id },
    });
  };

  const handleConfirmApply = () => {
    if (isMissingScene || isApplied || isApplying) return;
    setIsApplying(true);

    // 1. Actualización exclusiva del campo prompt de la toma original (sin modificar duración, título ni otros campos)
    dispatch({
      type: "SCENE_UPDATE",
      payload: {
        id: proposal.sceneId,
        patch: {
          prompt: cleanProposedPrompt,
        },
      },
    });

    // 2. Registro de propuesta aplicada
    dispatch({
      type: "PROPOSAL_APPLIED",
      payload: {
        projectId: proposal.projectId || projectId,
        proposalId: proposal.id,
      },
    });

    // 3. Si la propuesta proviene de una observación de continuidad, registrar decisión como aplicada
    if (proposal.observationId) {
      dispatch({
        type: "CONTINUITY_DECISION_SET",
        payload: {
          projectId: proposal.projectId || projectId,
          observationId: proposal.observationId,
          status: "applied",
        },
      });
    }

    setIsApplying(false);
    setIsConfirming(false);
  };

  const handleReactivate = () => {
    dispatch({
      type: "PROPOSAL_REACTIVATED",
      payload: { projectId: proposal.projectId || projectId, proposalId: proposal.id },
    });
  };

  return (
    <div
      data-testid={`assistant-prompt-update-proposal-${proposal.id}`}
      className={`mt-2 rounded-lg border transition-all ${
        isDismissed
          ? "border-[#262626] bg-[#121212]/60 opacity-70"
          : isApplied
          ? "border-[#059669]/30 bg-[#0e1713]"
          : "border-[#303030] bg-[#121212]"
      } p-3 space-y-2.5`}
    >
      {/* 1. Header con destino explícito y estado */}
      <div className="flex items-center justify-between gap-2 border-b border-[#222] pb-2">
        <div className="flex items-center gap-1.5 min-w-0">
          <Wand2 size={13} className="text-[#f5b544] shrink-0" />
          <span className="text-xs font-semibold text-[#f4f1ec] truncate">
            {proposal.title || `Actualizar prompt: ${destinationTitle}`}
          </span>
        </div>
        <div className="flex items-center gap-1.5 shrink-0">
          {isDismissed ? (
            <span
              data-testid={`assistant-prompt-update-status-dismissed-${proposal.id}`}
              className="rounded bg-[#252525] px-1.5 py-0.5 text-[10px] font-medium text-[#888] border border-[#333]"
            >
              {t.assistant?.dismissedTag || "Descartada"}
            </span>
          ) : isApplied ? (
            <span
              data-testid={`assistant-prompt-update-status-applied-${proposal.id}`}
              className="flex items-center gap-1 rounded bg-[#064e3b]/30 px-1.5 py-0.5 text-[10px] font-medium text-[#34d399] border border-[#059669]/30"
            >
              <CheckCircle2 size={10} />
              {t.assistant?.promptAppliedTag || "Prompt aplicado"}
            </span>
          ) : (
            <span className="rounded bg-[#f5b544]/15 px-1.5 py-0.5 text-[10px] font-medium text-[#f5b544]">
              {t.assistant?.quickActions?.prompt?.label || "Preparar prompt"}
            </span>
          )}
        </div>
      </div>

      {/* 2. Destino vinculado y avisos de selección / autorización */}
      <div className="space-y-1.5 text-xs">
        <div
          data-testid={`assistant-prompt-update-target-${proposal.id}`}
          className="flex items-center gap-1.5 text-[11px] text-[#aaa]"
        >
          <span className="text-[#777] font-medium">Toma de destino:</span>
          <span className="font-semibold text-[#f4f1ec]">«{destinationTitle}»</span>
          <span className="text-[10px] text-[#666] font-mono">({proposal.sceneId})</span>
        </div>

        {/* Aviso de selección cambiada en Storyboard */}
        {isSelectionMismatch && !isMissingScene && (
          <div
            data-testid={`assistant-prompt-update-selection-mismatch-${proposal.id}`}
            className="flex items-center gap-1.5 rounded bg-[#1e1b13] border border-[#f5b544]/30 px-2 py-1 text-[11px] text-[#f5b544]"
          >
            <Info size={12} className="shrink-0" />
            <span>
              Vinculada a la toma original «{destinationTitle}» (la selección actual en Storyboard es otra toma).
            </span>
          </div>
        )}

        {/* Bloqueo si la toma fue eliminada o pertenece a otro proyecto */}
        {isMissingScene && (
          <div
            data-testid={`assistant-prompt-update-unauthorized-${proposal.id}`}
            className="flex items-center gap-1.5 rounded bg-[#2b1416] border border-[#e5484d]/40 p-2 text-xs text-[#ffb4b7]"
          >
            <AlertCircle size={13} className="shrink-0 text-[#ef4444]" />
            <span>
              {isUnauthorizedProject
                ? `La propuesta pertenece a otro proyecto (${proposal.projectId}) y no puede aplicarse aquí.`
                : `La toma original (ID: ${proposal.sceneId}) fue eliminada o no pertenece a este proyecto.`}
            </span>
          </div>
        )}

        {/* Advertencia si el prompt de la toma cambió durante la espera */}
        {isPromptModified && !isMissingScene && !isApplied && (
          <div
            data-testid={`assistant-prompt-update-prompt-changed-${proposal.id}`}
            className="flex flex-col gap-1 rounded bg-[#2a1d0d] border border-amber-500/40 p-2 text-xs text-amber-200"
          >
            <div className="flex items-center gap-1.5 font-medium text-amber-300">
              <AlertTriangle size={13} className="shrink-0 text-amber-400" />
              <span>Aviso: El prompt de la toma ha cambiado desde que se preparó esta propuesta.</span>
            </div>
            <p className="text-[11px] text-amber-200/90 leading-relaxed">
              El contenido actual de la toma es diferente al original. Revisa las diferencias antes de sobrescribirlo.
            </p>
          </div>
        )}
      </div>

      {/* 3. Comparativa completa y legible: Prompt actual vs Prompt propuesto */}
      {!isDismissed && (
        <div className="space-y-2">
          {/* Prompt actual */}
          <div className="rounded border border-[#262626] bg-[#161616] p-2 text-xs">
            <div className="text-[10px] font-semibold text-[#888] uppercase tracking-wider mb-1">
              Prompt actual en la toma:
            </div>
            <div
              data-testid={`assistant-prompt-update-current-${proposal.id}`}
              className="font-mono text-[11px] leading-relaxed text-[#aaa] whitespace-pre-wrap select-text"
            >
              {currentPromptInScene || proposal.originalPrompt || "(Sin prompt previo)"}
            </div>
          </div>

          {/* Prompt propuesto */}
          <div className="rounded border border-[#3d321d] bg-[#1a1711] p-2 text-xs">
            <div className="flex items-center justify-between gap-2 mb-1">
              <div className="text-[10px] font-semibold text-[#f5b544] uppercase tracking-wider">
                Prompt propuesto:
              </div>
              {suggestedDuration && (
                <span
                  data-testid={`assistant-prompt-update-suggested-duration-${proposal.id}`}
                  className="rounded bg-[#2a2417] px-1.5 py-0.5 text-[10px] text-[#f5b544] border border-[#f5b544]/30"
                  title="Sugerencia consultiva, no modifica la duración de la toma"
                >
                  Duración sugerida: {suggestedDuration}s (consultiva)
                </span>
              )}
            </div>
            <div
              data-testid={`assistant-prompt-update-proposed-${proposal.id}`}
              className="font-mono text-[11px] leading-relaxed text-[#f4f1ec] whitespace-pre-wrap select-text"
            >
              {cleanProposedPrompt}
            </div>
          </div>
        </div>
      )}

      {/* 4. Panel de confirmación explícita previa a la aplicación */}
      {isConfirming && !isApplied && !isMissingScene && (
        <div
          data-testid={`assistant-prompt-update-confirm-panel-${proposal.id}`}
          className="rounded border border-[#f5b544]/40 bg-[#201c13] p-2.5 space-y-2 text-xs"
        >
          <div className="flex items-center gap-1.5 font-medium text-[#f5b544]">
            <Sparkles size={13} />
            <span>¿Confirmar y aplicar este prompt a «{destinationTitle}»?</span>
          </div>
          <p className="text-[11px] text-[#ccc] leading-relaxed">
            Se actualizará exclusivamente el campo de prompt de la toma original. El título, la duración, las referencias y los medios asignados se mantendrán intactos.
          </p>
          <div className="flex items-center gap-2 pt-1">
            <button
              type="button"
              data-testid={`assistant-prompt-update-confirm-btn-${proposal.id}`}
              onClick={handleConfirmApply}
              disabled={isApplying}
              className="v-btn-primary v-btn-sm flex-1 justify-center disabled:opacity-50"
            >
              Confirmar y aplicar
            </button>
            <button
              type="button"
              data-testid={`assistant-prompt-update-cancel-confirm-btn-${proposal.id}`}
              onClick={() => setIsConfirming(false)}
              className="v-btn-ghost v-btn-sm px-3"
            >
              Volver
            </button>
          </div>
        </div>
      )}

      {/* 5. Barra de acciones principales (si no está en modo confirmación) */}
      {!isConfirming && (
        <div className="flex items-center justify-between gap-2 pt-1 border-t border-[#222]">
          <div className="flex items-center gap-2">
            {!isApplied && !isDismissed && (
              <button
                type="button"
                data-testid={`assistant-prompt-update-apply-btn-${proposal.id}`}
                onClick={handleApplyClick}
                disabled={isMissingScene}
                className="v-btn-primary v-btn-sm disabled:opacity-40 disabled:cursor-not-allowed"
              >
                Aplicar prompt
              </button>
            )}
            {!isApplied && !isDismissed && (
              <button
                type="button"
                data-testid={`assistant-prompt-update-cancel-btn-${proposal.id}`}
                onClick={handleCancelClick}
                className="v-btn-ghost v-btn-sm text-[#888] hover:text-[#f4f1ec]"
              >
                Cancelar
              </button>
            )}
          </div>

          <div>
            {isDismissed ? (
              <button
                type="button"
                data-testid={`assistant-prompt-update-reactivate-${proposal.id}`}
                onClick={handleReactivate}
                className="text-[11px] text-[#aaa] hover:text-[#f4f1ec] transition-colors cursor-pointer"
              >
                {t.assistant?.reactivateProposal || "Reactivar"}
              </button>
            ) : null}
          </div>
        </div>
      )}
    </div>
  );
};

const SceneCreationProposalCard = ({ proposal, projectId, data: propsData, messageId }) => {
  const studio = useStudio();
  const dispatch = studio?.dispatch;
  const data = propsData || studio?.data;

  const scenes = useMemo(() => (Array.isArray(proposal?.scenes) ? proposal.scenes : []), [proposal?.scenes]);

  const activeScript = data?.activeScript || (data?.scripts || []).find((sc) => sc.id === data?.project?.activeScriptId || sc.isActive);
  const proposalScriptId = proposal?.scriptId;
  const isScriptMismatch = Boolean(
    proposalScriptId && (!activeScript || activeScript.id !== proposalScriptId)
  );
  const matchingSavedScript = proposalScriptId
    ? (data?.scripts || []).find((sc) => sc.id === proposalScriptId)
    : null;

  const initialApplied = useMemo(() => {
    if (Array.isArray(proposal?.appliedIndices)) return proposal.appliedIndices;
    if (proposal?.applied) return scenes.map((_, i) => i);
    return [];
  }, [proposal?.appliedIndices, proposal?.applied, scenes]);

  const cardRef = useRef(null);
  const portalContainer =
    typeof process !== "undefined" && process.env.NODE_ENV === "test"
      ? cardRef.current?.closest("body > div") || undefined
      : undefined;

  const [appliedIdx, setAppliedIdx] = useState(initialApplied);
  const [expandedIdx, setExpandedIdx] = useState(null);
  const [isReviewing, setIsReviewing] = useState(false);
  const [confirmingSceneIdx, setConfirmingSceneIdx] = useState(null);
  const [confirmingAll, setConfirmingAll] = useState(false);

  useEffect(() => {
    if (Array.isArray(proposal?.appliedIndices)) {
      setAppliedIdx(proposal.appliedIndices);
    } else if (proposal?.applied) {
      setAppliedIdx(scenes.map((_, i) => i));
    }
  }, [proposal?.appliedIndices, proposal?.applied, scenes]);

  if (!proposal.scenes || !Array.isArray(proposal.scenes)) {
    return null;
  }

  const isDismissed = Boolean(proposal.dismissed);
  const totalScenes = proposal.scenes.length;
  const isFullyApplied = Boolean(proposal.applied || (totalScenes > 0 && appliedIdx.length >= totalScenes));
  const isPartiallyApplied = !isFullyApplied && appliedIdx.length > 0;
  const totalDuration = proposal.scenes.reduce((acc, s) => acc + (Number(s.duration) || 3), 0);
  const reusedScenesCount = proposal.scenes.filter((s) => Boolean(s.isReuse || s.existingSceneId)).length;
  const newScenesCount = totalScenes - reusedScenesCount;
  const pendingScenesCount = Math.max(0, totalScenes - appliedIdx.length);

  const requestApplyOne = (i) => {
    if (isScriptMismatch) return;
    setConfirmingSceneIdx(i);
    setConfirmingAll(false);
  };

  const cancelApplyOne = () => {
    setConfirmingSceneIdx(null);
  };

  const executeApplyOne = (s, i) => {
    if (isScriptMismatch) return;
    const isReuse = Boolean(s.isReuse || s.existingSceneId);
    if (!isReuse) {
      dispatch({
        type: "SCENE_CREATE",
        payload: {
          projectId,
          title: s.title,
          prompt: s.prompt,
          duration: s.duration || null,
          action: s.action || "",
          camera: s.camera || "",
          planned_sound: s.sounds || s.planned_sound || "",
          missingReferences: Array.isArray(s.missingReferences)
            ? s.missingReferences
            : Array.isArray(s.pendingReferences)
            ? s.pendingReferences
            : [],
          scriptId: proposal.scriptId || s.scriptId || null,
          scriptVersionId: proposal.scriptVersionId || s.scriptVersionId || null,
          ...(Array.isArray(s.referenceIds) && s.referenceIds.length > 0 ? { referenceIds: s.referenceIds } : {}),
        },
      });
    }
    dispatch({
      type: "PROPOSAL_SCENE_APPLIED",
      payload: { projectId, proposalId: proposal.id, sceneIndex: i },
    });
    setAppliedIdx((x) => (x.includes(i) ? x : [...x, i]));
    setConfirmingSceneIdx(null);
  };

  const requestApplyAll = () => {
    if (isScriptMismatch) return;
    setConfirmingAll(true);
    setConfirmingSceneIdx(null);
  };

  const cancelApplyAll = () => {
    setConfirmingAll(false);
  };

  const executeApplyAll = () => {
    if (isScriptMismatch) return;
    const allIndices = proposal.scenes.map((_, idx) => idx);
    proposal.scenes.forEach((s, i) => {
      if (!appliedIdx.includes(i)) {
        const isReuse = Boolean(s.isReuse || s.existingSceneId);
        if (!isReuse) {
          dispatch({
            type: "SCENE_CREATE",
            payload: {
              projectId,
              title: s.title,
              prompt: s.prompt,
              duration: s.duration || null,
              action: s.action || "",
              camera: s.camera || "",
              planned_sound: s.sounds || s.planned_sound || "",
              missingReferences: Array.isArray(s.missingReferences)
                ? s.missingReferences
                : Array.isArray(s.pendingReferences)
                ? s.pendingReferences
                : [],
              scriptId: proposal.scriptId || s.scriptId || null,
              scriptVersionId: proposal.scriptVersionId || s.scriptVersionId || null,
              ...(Array.isArray(s.referenceIds) && s.referenceIds.length > 0 ? { referenceIds: s.referenceIds } : {}),
            },
          });
        }
      }
    });

    dispatch({
      type: "PROPOSAL_APPLIED",
      payload: { projectId, proposalId: proposal.id },
    });
    setAppliedIdx(allIndices);
    setConfirmingAll(false);
    setIsReviewing(false);
  };

  const handleDismiss = () => {
    dispatch({ type: "PROPOSAL_DISMISSED", payload: { projectId, proposalId: proposal.id } });
    setIsReviewing(false);
    setConfirmingSceneIdx(null);
    setConfirmingAll(false);
  };

  const handleReactivate = () => {
    dispatch({ type: "PROPOSAL_REACTIVATED", payload: { projectId, proposalId: proposal.id } });
  };

  const toggleDetail = (i) => {
    setExpandedIdx((curr) => (curr === i ? null : i));
  };

  return (
    <div
      ref={cardRef}
      data-testid={`assistant-proposal-${proposal.id}`}
      className={`mt-2 rounded-lg border transition-all ${
        isDismissed
          ? "border-[#262626] bg-[#121212]/60 opacity-70"
          : isFullyApplied
          ? "border-[#059669]/30 bg-[#0e1713]"
          : "border-[#303030] bg-[#121212]"
      } p-2.5`}
    >
      {/* Aviso bloqueante de discrepancia de guion activo */}
      {isScriptMismatch && (
        <div
          data-testid={`assistant-proposal-script-mismatch-${proposal.id}`}
          className="mb-2.5 rounded-lg border border-amber-500/40 bg-amber-500/10 p-2.5 text-xs text-amber-200 space-y-1.5"
        >
          <div className="flex items-center gap-1.5 font-semibold text-amber-400">
            <AlertTriangle size={14} />
            <span>Propuesta bloqueada: guion no coincidente</span>
          </div>
          <p className="text-[11px] text-amber-200/90 leading-relaxed">
            Esta propuesta fue creada para «{proposal.scriptTitle || "Borrador de guion"}» ({proposal.scriptVersionId || "versión previa"}).
            {!activeScript
              ? " Actualmente el proyecto no tiene ningún guion activo."
              : ` El guion activo actual del proyecto es «${activeScript.title}» (${activeScript.versionId || "v1"}).`}
            {" "}Su incorporación al Storyboard está bloqueada para preservar la coherencia narrativa.
          </p>
          <div className="flex flex-wrap items-center gap-2 pt-0.5">
            {matchingSavedScript ? (
              <button
                type="button"
                data-testid={`assistant-proposal-reactivate-script-${proposal.id}`}
                onClick={() => {
                  dispatch({
                    type: "SCRIPT_SET_ACTIVE",
                    payload: { projectId, scriptId: matchingSavedScript.id },
                  });
                }}
                className="rounded bg-amber-500/20 hover:bg-amber-500/30 text-amber-300 border border-amber-500/40 px-2 py-0.5 text-[11px] font-medium transition-colors cursor-pointer"
              >
                Volver a activar «{matchingSavedScript.title}» ({matchingSavedScript.versionId})
              </button>
            ) : (
              <span className="text-[10px] text-amber-400/80 italic">
                La versión original del guion ya no está guardada. Prepara una propuesta nueva desde el guion activo.
              </span>
            )}
          </div>
        </div>
      )}

      {/* 1. Header con título, badges de estado */}
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-1.5 truncate">
          <span className="text-xs font-medium text-[#f4f1ec] truncate">{proposal.title}</span>
          {proposal.scriptVersionId && (
            <span className="rounded bg-[#202020] px-1 py-0.2 text-[9px] font-mono text-[#aaa] border border-[#333]">
              {proposal.scriptVersionId}
            </span>
          )}
        </div>
        <div className="flex items-center gap-1.5 shrink-0">
          {isDismissed ? (
            <span
              data-testid={`assistant-proposal-status-dismissed-${proposal.id}`}
              className="rounded bg-[#252525] px-1.5 py-0.5 text-[10px] font-medium text-[#888] border border-[#333]"
            >
              {t.assistant.dismissedTag || "Descartada"}
            </span>
          ) : isFullyApplied ? (
            <span
              data-testid={`assistant-proposal-status-applied-${proposal.id}`}
              className="flex items-center gap-1 rounded bg-[#064e3b]/30 px-1.5 py-0.5 text-[10px] font-medium text-[#34d399] border border-[#059669]/30"
            >
              <CheckCircle2 size={10} />
              {t.assistant.appliedTag || "Aplicada"}
            </span>
          ) : isPartiallyApplied ? (
            <span className="rounded bg-[#78350f]/20 px-1.5 py-0.5 text-[10px] font-medium text-[#f5b544] border border-[#b45309]/30">
              {t.assistant.partiallyAppliedTag ? t.assistant.partiallyAppliedTag(appliedIdx.length, totalScenes) : `${appliedIdx.length}/${totalScenes} aplicadas`}
            </span>
          ) : (
            <span className="rounded bg-[#f5b544]/15 px-1.5 py-0.5 text-[10px] font-medium text-[#f5b544]">
              {t.assistant.proposalTitle || "Propuesta"}
            </span>
          )}
        </div>
      </div>

      {/* Resumen de tomas y acciones de descarte/reactivación */}
      <div className="mt-1 flex items-center justify-between gap-2 text-[10px] text-[#888]">
        <span>
          {t.assistant.proposalScenesSummary
            ? t.assistant.proposalScenesSummary(totalScenes, totalDuration)
            : `${totalScenes} tomas · ~${totalDuration}s total`}
        </span>
        <div className="flex items-center gap-2">
          {isDismissed ? (
            <button
              type="button"
              data-testid={`assistant-proposal-reactivate-${proposal.id}`}
              onClick={handleReactivate}
              className="text-[#aaa] hover:text-[#f4f1ec] transition-colors cursor-pointer"
            >
              {t.assistant.reactivateProposal || "Reactivar"}
            </button>
          ) : !isFullyApplied ? (
            <button
              type="button"
              data-testid={`assistant-proposal-dismiss-${proposal.id}`}
              onClick={handleDismiss}
              className="text-[#777] hover:text-[#ff8080] transition-colors cursor-pointer"
            >
              {t.assistant.dismissProposal || "Descartar"}
            </button>
          ) : null}
        </div>
      </div>

      {/* Indicador visual de confirmación pendiente */}
      {!isFullyApplied && !isDismissed && (
        <div
          data-testid={`assistant-proposal-pending-notice-${proposal.id}`}
          className="mt-2 flex items-center justify-between gap-2 rounded-md border border-amber-500/30 bg-amber-500/10 px-2.5 py-1 text-xs text-amber-300 font-medium"
        >
          <div className="flex items-center gap-1.5">
            <AlertCircle size={13} className="shrink-0 text-amber-400" />
            <span>Pendiente de tu confirmación</span>
          </div>
          <span className="text-[10px] text-amber-300/80">
            {pendingScenesCount} {pendingScenesCount === 1 ? "toma pendiente" : "tomas pendientes"}
          </span>
        </div>
      )}

      {/* Botón principal visible: Revisar e incorporar tomas */}
      {!isFullyApplied && !isDismissed && (
        <button
          type="button"
          data-testid={`assistant-proposal-review-btn-${proposal.id}`}
          onClick={() => setIsReviewing(true)}
          className="mt-2.5 inline-flex w-full items-center justify-center gap-2 rounded-lg bg-[#f5b544] hover:bg-[#ffc45a] active:scale-[0.99] text-[#1a1206] px-4 py-2 text-xs font-semibold shadow-md transition-all cursor-pointer"
        >
          <Sparkles size={14} className="shrink-0" />
          <span>Revisar e incorporar tomas</span>
        </button>
      )}

      {!isFullyApplied && !isDismissed && (
        <p className="mt-1.5 text-[10px] text-[#888]" data-testid={`assistant-proposal-review-hint-${proposal.id}`}>
          {t.assistant.reviewHint || "Revisa la lista completa en una ventana amplia antes de incorporar las tomas al guion gráfico."}
        </p>
      )}

      {/* 2. Modal Independiente de Revisión Amplio y Adaptable */}
      <Dialog open={isReviewing} onOpenChange={(open) => !open && setIsReviewing(false)}>
        <DialogContent
          data-testid={`assistant-proposal-review-modal-${proposal.id}`}
          container={portalContainer}
          hideCloseButton={true}
          className="w-[95vw] sm:max-w-4xl max-h-[92vh] sm:max-h-[88vh] flex flex-col p-0 border border-[#303030] bg-[#141414] text-[#f4f1ec] overflow-hidden rounded-xl shadow-2xl z-50"
        >
          {/* Cabecera Fija */}
          <DialogHeader className="shrink-0 border-b border-[#242424] bg-[#181818] p-4 sm:p-5 flex flex-col gap-2.5 text-left">
            <div className="flex items-center justify-between gap-3">
              <div className="flex items-center gap-2.5 min-w-0">
                <div className="flex h-8 w-8 items-center justify-center rounded-lg border border-[#f5b544]/30 bg-[#f5b544]/10 text-[#f5b544] shrink-0">
                  <Sparkles size={16} />
                </div>
                <div className="min-w-0">
                  <DialogTitle className="text-sm sm:text-base font-semibold text-[#f4f1ec] truncate">
                    {t.assistant.reviewProposal || "Revisar propuesta"}: {proposal.title || "Storyboard"}
                  </DialogTitle>
                  <DialogDescription className="text-[11px] text-[#888] mt-0.5 truncate">
                    {proposal.scriptTitle ? `Guion: «${proposal.scriptTitle}»` : "Revisa y confirma las tomas antes de incorporarlas al proyecto."}
                  </DialogDescription>
                </div>
              </div>
              <button
                type="button"
                data-testid={`assistant-proposal-cancel-review-${proposal.id}`}
                onClick={() => setIsReviewing(false)}
                className="rounded-lg p-1.5 text-[#888] hover:text-[#f4f1ec] hover:bg-[#262626] transition-colors cursor-pointer"
                title="Cerrar revisión"
                aria-label="Cerrar"
              >
                <X size={16} />
              </button>
            </div>

            {/* Resumen de tomas en cabecera */}
            <div className="flex flex-wrap items-center gap-2 text-xs text-[#aaa] pt-1">
              <span className="font-semibold text-[#f4f1ec]">{totalScenes} tomas</span>
              <span className="text-[#555]">·</span>
              <span className="text-[#38bdf8] font-medium">{reusedScenesCount} {reusedScenesCount === 1 ? "reutilizada" : "reutilizadas"}</span>
              <span className="text-[#555]">·</span>
              <span className="text-amber-400 font-medium">{newScenesCount} {newScenesCount === 1 ? "nueva" : "nuevas"}</span>
              <span className="text-[#555]">·</span>
              <span className="text-[#ccc]">~{totalDuration}s total</span>
              {proposal.scriptVersionId && (
                <span className="rounded bg-[#202020] px-1.5 py-0.5 text-[10px] font-mono text-[#aaa] border border-[#333]">
                  {proposal.scriptVersionId}
                </span>
              )}
              {!isFullyApplied && !isDismissed && (
                <span className="rounded bg-amber-500/15 border border-amber-500/30 text-amber-300 px-2 py-0.5 text-[10px] font-medium flex items-center gap-1">
                  <AlertCircle size={11} /> Pendiente de tu confirmación
                </span>
              )}
            </div>
          </DialogHeader>

          {/* Cuerpo con desplazamiento vertical único */}
          <div className="flex-1 overflow-y-auto p-4 sm:p-5 space-y-3 overscroll-contain">
            {/* Alerta si hay discrepancia de guion activo */}
            {isScriptMismatch && (
              <div className="rounded-lg border border-amber-500/40 bg-amber-500/10 p-3 text-xs text-amber-200 space-y-1.5">
                <div className="flex items-center gap-1.5 font-semibold text-amber-400">
                  <AlertTriangle size={14} />
                  <span>Propuesta bloqueada: guion no coincidente</span>
                </div>
                <p className="text-[11px] leading-relaxed text-amber-200/90">
                  Esta propuesta pertenece a «{proposal.scriptTitle || "Borrador de guion"}» ({proposal.scriptVersionId || "v1"}).
                  El guion activo actual del proyecto es «{activeScript?.title || "ninguno"}». Su incorporación está bloqueada para preservar la coherencia narrativa.
                </p>
              </div>
            )}

            {/* Lista detallada de tomas */}
            {proposal.scenes.map((s, i) => {
              const isApplied = isFullyApplied || appliedIdx.includes(i);
              const isReuse = Boolean(s.isReuse || s.existingSceneId);
              return (
                <div
                  key={i}
                  className={`rounded-lg border p-3.5 transition-all ${
                    isApplied
                      ? "border-[#059669]/30 bg-[#064e3b]/10"
                      : "border-[#282828] bg-[#1a1a1a]"
                  }`}
                >
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <div className="flex items-center gap-2 min-w-0">
                      <span className="rounded bg-[#222] px-1.5 py-0.5 text-[10px] font-mono text-[#888] border border-[#333]">
                        #{i + 1}
                      </span>
                      <span className="font-semibold text-xs sm:text-sm text-[#f4f1ec] truncate">{s.title}</span>
                      {isReuse ? (
                        <span
                          data-testid={`scene-reuse-badge-${i}`}
                          className="rounded bg-sky-500/20 text-sky-300 border border-sky-500/40 px-2 py-0.5 text-[10px] font-medium"
                        >
                          Toma reutilizada
                        </span>
                      ) : (
                        <span className="rounded bg-amber-500/15 text-amber-300 border border-amber-500/30 px-2 py-0.5 text-[10px] font-medium">
                          Nueva toma
                        </span>
                      )}
                    </div>

                    <div className="flex items-center gap-2 shrink-0">
                      <span className="rounded bg-[#242424] px-2 py-0.5 text-[10px] font-mono text-[#aaa] border border-[#333]">
                        {s.duration || 3}s
                      </span>
                      {isApplied ? (
                        <span className="flex items-center gap-1 text-[11px] font-medium text-[#34d399]">
                          <CheckCircle2 size={12} /> Ya incorporada
                        </span>
                      ) : (
                        <button
                          type="button"
                          data-testid={`assistant-proposal-apply-${proposal.id}-${i}`}
                          disabled={isApplied || isDismissed || confirmingSceneIdx === i || isScriptMismatch}
                          onClick={() => requestApplyOne(i)}
                          className="rounded border border-[#383838] bg-[#222] hover:bg-[#2a2a2a] px-2 py-1 text-[10px] font-medium text-[#d4d4d4] hover:text-white transition-colors cursor-pointer disabled:opacity-40"
                        >
                          {isReuse ? "Reutilizar toma" : "Añadir toma"}
                        </button>
                      )}
                    </div>
                  </div>

                  {/* Prompt cinemático completo */}
                  <div className="mt-2.5 rounded-md bg-[#0f0f0f] border border-[#222] p-2.5 font-mono text-xs leading-relaxed text-[#ccc] whitespace-pre-wrap select-text">
                    {s.prompt}
                  </div>

                  {/* Detalles de dirección si existen */}
                  {(s.action || s.camera || s.sounds) && (
                    <div className="mt-2 grid grid-cols-1 sm:grid-cols-3 gap-2 text-[11px] text-[#aaa] bg-[#141414] p-2 rounded border border-[#222]">
                      {s.action && <div><span className="text-[#f5b544] font-medium">Acción:</span> {s.action}</div>}
                      {s.camera && <div><span className="text-[#f5b544] font-medium">Cámara:</span> {s.camera}</div>}
                      {s.sounds && <div><span className="text-[#f5b544] font-medium">Sonido:</span> {s.sounds}</div>}
                    </div>
                  )}

                  {/* Referencias */}
                  {Array.isArray(s.referenceIds) && s.referenceIds.length > 0 && (
                    <div className="mt-2 flex flex-wrap items-center gap-1 text-[10px] text-[#888]">
                      <span>{t.assistant.referencedAssets || "Referencias:"}</span>
                      {s.referenceIds.map((refId) => (
                        <span key={refId} className="rounded bg-[#222] px-1.5 py-0.5 border border-[#333] text-[#bbb]">
                          {refId}
                        </span>
                      ))}
                    </div>
                  )}
                  {Array.isArray(s.missingReferences) && s.missingReferences.length > 0 && (
                    <div className="mt-2 flex flex-wrap items-center gap-1 text-[10px] text-amber-400">
                      <span>Referencias faltantes:</span>
                      {s.missingReferences.map((ref, idx) => (
                        <span key={idx} className="rounded bg-amber-500/10 px-1.5 py-0.5 border border-amber-500/30 text-amber-300">
                          {ref}
                        </span>
                      ))}
                    </div>
                  )}

                  {/* Confirmación individual de la escena */}
                  {confirmingSceneIdx === i && !isApplied && (
                    <div
                      data-testid={`assistant-proposal-confirm-box-${proposal.id}-${i}`}
                      className="mt-2.5 rounded-md border border-[#f5b544]/40 bg-[#1e170c] p-2.5 text-xs flex flex-col gap-1.5"
                    >
                      <div className="font-medium text-[#f5b544] text-[11px]">
                        {isReuse ? "¿Reutilizar toma en el Storyboard?" : "¿Añadir toma al Storyboard?"}
                      </div>
                      <div className="text-[10px] text-[#bbb]">
                        «{s.title}» ({s.duration || 3}s) {isReuse && "· (No creará duplicado ni modificará su prompt original)"}
                      </div>
                      <div className="flex items-center gap-2 pt-0.5">
                        <button
                          type="button"
                          data-testid={`assistant-proposal-confirm-apply-${proposal.id}-${i}`}
                          onClick={() => executeApplyOne(s, i)}
                          className="v-btn-primary v-btn-sm text-[10px] py-0.5 px-2.5 justify-center"
                        >
                          {t.common.confirm || "Confirmar"}
                        </button>
                        <button
                          type="button"
                          data-testid={`assistant-proposal-cancel-apply-${proposal.id}-${i}`}
                          onClick={cancelApplyOne}
                          className="v-btn-ghost v-btn-sm text-[10px] py-0.5 px-2.5"
                        >
                          {t.common.cancel || "Cancelar"}
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Pie Fijo con Cancelar y Confirmar e incorporar tomas */}
          <DialogFooter className="shrink-0 border-t border-[#242424] bg-[#181818] p-4 sm:p-5 flex flex-col sm:flex-row items-center justify-between gap-3 text-left">
            <div className="text-xs text-[#888]">
              {pendingScenesCount > 0 ? (
                <span>
                  <strong className="text-amber-400 font-semibold">{pendingScenesCount}</strong> de {totalScenes} tomas pendientes de incorporar
                </span>
              ) : (
                <span className="text-[#34d399] font-medium flex items-center gap-1">
                  <CheckCircle2 size={12} /> Todas las tomas han sido incorporadas al Storyboard
                </span>
              )}
            </div>

            <div className="flex items-center gap-2.5 w-full sm:w-auto">
              <button
                type="button"
                data-testid={`assistant-proposal-cancel-review-footer-${proposal.id}`}
                onClick={() => setIsReviewing(false)}
                className="rounded-lg border border-[#333] bg-[#202020] hover:bg-[#282828] text-[#d8d4cd] px-4 py-2 text-xs font-medium transition-colors flex-1 sm:flex-initial text-center cursor-pointer"
              >
                {t.common?.cancel || "Cancelar"}
              </button>

              <button
                type="button"
                data-testid={`assistant-proposal-confirm-all-${proposal.id}`}
                onClick={executeApplyAll}
                disabled={isFullyApplied || isScriptMismatch || pendingScenesCount === 0}
                className="rounded-lg bg-[#f5b544] hover:bg-[#ffc45a] active:scale-[0.98] text-[#1a1206] px-5 py-2 text-xs font-semibold shadow-md transition-all disabled:opacity-40 disabled:pointer-events-none flex items-center justify-center gap-1.5 flex-1 sm:flex-initial cursor-pointer"
              >
                <Sparkles size={14} className="shrink-0" />
                <span>{t.assistant.confirmAndApplyAll || "Confirmar e incorporar tomas"}</span>
              </button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* 3. Lista de escenas con vista previa e inspección inline */}
      {!isReviewing && (
        <ul className="mt-2 flex flex-col gap-1.5">
          {proposal.scenes.map((s, i) => {
            const isApplied = isFullyApplied || appliedIdx.includes(i);
            const isExpanded = expandedIdx === i;
            const isReuse = Boolean(s.isReuse || s.existingSceneId);

            return (
              <li
                key={i}
                className={`rounded border transition-colors p-1.5 ${
                  isApplied
                    ? "border-[#202020] bg-[#101010]/50"
                    : "border-[#262626] bg-[#161616]"
                }`}
              >
                <div className="flex items-start justify-between gap-2 text-xs">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-1.5 flex-wrap">
                      <span className="font-medium text-[#d8d4cd]">{s.title}</span>
                      <span className="rounded bg-[#202020] px-1 py-0.2 text-[9px] text-[#777]">
                        {s.duration || 3}s
                      </span>
                      {isReuse && (
                        <span
                          data-testid={`scene-reuse-badge-${i}`}
                          className="rounded bg-sky-500/20 text-sky-300 border border-sky-500/40 px-1 py-0.2 text-[9px] font-medium"
                        >
                          Toma reutilizada
                        </span>
                      )}
                    </div>

                    {isExpanded ? (
                      <div
                        data-testid={`assistant-proposal-detail-${proposal.id}-${i}`}
                        className="mt-1.5 rounded bg-[#111] p-1.5 font-mono text-[10px] leading-relaxed text-[#ccc] whitespace-pre-wrap"
                      >
                        {s.prompt}
                        {(s.action || s.camera || s.sounds) && (
                          <div className="mt-1.5 space-y-0.5 text-[10px] font-sans text-[#aaa] border-t border-[#222] pt-1">
                            {s.action && <div><strong className="text-[#ccc]">Acción:</strong> {s.action}</div>}
                            {s.camera && <div><strong className="text-[#ccc]">Cámara:</strong> {s.camera}</div>}
                            {s.sounds && <div><strong className="text-[#ccc]">Sonido:</strong> {s.sounds}</div>}
                          </div>
                        )}
                        {Array.isArray(s.referenceIds) && s.referenceIds.length > 0 && (
                          <div className="mt-1 flex flex-wrap gap-1 text-[9px] text-[#888]">
                            <span>Referencias:</span>
                            {s.referenceIds.map((refId) => (
                              <span key={refId} className="rounded bg-[#222] px-1 border border-[#333] text-[#aaa]">
                                {refId}
                              </span>
                            ))}
                          </div>
                        )}
                        {Array.isArray(s.missingReferences) && s.missingReferences.length > 0 && (
                          <div className="mt-1 flex flex-wrap gap-1 text-[9px] text-amber-400">
                            <span>Referencias faltantes:</span>
                            {s.missingReferences.map((ref, idx) => (
                              <span key={idx} className="rounded bg-amber-500/10 px-1 border border-amber-500/30 text-amber-300">
                                {ref}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className="line-clamp-2 text-[#999] text-[11px] mt-0.5">{s.prompt}</div>
                    )}
                  </div>

                  <div className="flex items-center gap-1 shrink-0">
                    <button
                      type="button"
                      data-testid={`assistant-proposal-toggle-detail-${proposal.id}-${i}`}
                      onClick={() => toggleDetail(i)}
                      className="rounded border border-[#303030] p-1 text-[#888] hover:text-[#f4f1ec] transition-colors"
                      title={isExpanded ? t.assistant.hideDetails || "Ocultar detalle" : t.assistant.showDetails || "Ver detalle"}
                      aria-label={isExpanded ? "Ocultar detalle" : "Ver detalle"}
                    >
                      {isExpanded ? <ChevronUp size={11} /> : <Eye size={11} />}
                    </button>

                    <button
                      data-testid={`assistant-proposal-apply-${proposal.id}-${i}`}
                      disabled={isApplied || isDismissed || confirmingSceneIdx === i || isScriptMismatch}
                      onClick={() => requestApplyOne(i)}
                      className="rounded border border-[#303030] px-1.5 py-0.5 text-[10px] text-[#a3a3a3] hover:text-[#f4f1ec] disabled:opacity-40 transition-colors"
                    >
                      {isApplied ? t.common.applied : t.assistant.applyOne}
                    </button>
                  </div>
                </div>

                {/* Confirmación previa para aplicar una escena individual */}
                {confirmingSceneIdx === i && !isApplied && (
                  <div
                    data-testid={`assistant-proposal-confirm-box-${proposal.id}-${i}`}
                    className="mt-2 rounded-md border border-[#f5b544]/40 bg-[#1e170c] p-2 text-xs flex flex-col gap-1.5"
                  >
                    <div className="font-medium text-[#f5b544] text-[11px]">
                      {isReuse ? "¿Reutilizar toma en el proyecto?" : (t.assistant.confirmSceneAdd || "¿Añadir toma al proyecto?")}
                    </div>
                    <div className="text-[10px] text-[#bbb]">
                      "{s.title}" ({s.duration || 3}s) {isReuse && "· (No creará duplicado ni modificará su prompt)"}
                    </div>
                    <div className="flex items-center gap-1.5 pt-0.5">
                      <button
                        type="button"
                        data-testid={`assistant-proposal-confirm-apply-${proposal.id}-${i}`}
                        onClick={() => executeApplyOne(s, i)}
                        className="v-btn-primary v-btn-sm text-[10px] py-0.5 px-2 justify-center"
                      >
                        {t.common.confirm || "Confirmar"}
                      </button>
                      <button
                        type="button"
                        data-testid={`assistant-proposal-cancel-apply-${proposal.id}-${i}`}
                        onClick={cancelApplyOne}
                        className="v-btn-ghost v-btn-sm text-[10px] py-0.5 px-2"
                      >
                        {t.common.cancel || "Cancelar"}
                      </button>
                    </div>
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}

      {/* 4. Botón inferior Aplicar Todas con confirmación previa */}
      {!isReviewing && (
        confirmingAll ? (
          <div
            data-testid={`assistant-proposal-confirm-all-box-${proposal.id}`}
            className="mt-2 rounded-md border border-[#f5b544]/40 bg-[#1e170c] p-2.5 text-xs flex flex-col gap-2"
          >
            <div className="font-medium text-[#f5b544] text-xs">
              {t.assistant.confirmAllScenesPrompt
                ? t.assistant.confirmAllScenesPrompt(proposal.scenes.length - appliedIdx.length)
                : `¿Incorporar ${proposal.scenes.length - appliedIdx.length} escenas al Storyboard?`}
            </div>
            <div className="flex items-center gap-2">
              <button
                type="button"
                data-testid={`assistant-proposal-confirm-all-${proposal.id}`}
                onClick={executeApplyAll}
                className="v-btn-primary v-btn-sm flex-1 justify-center text-xs py-1"
              >
                {t.assistant.confirmAndApplyAll || "Confirmar e incorporar todas"}
              </button>
              <button
                type="button"
                data-testid={`assistant-proposal-cancel-all-${proposal.id}`}
                onClick={cancelApplyAll}
                className="v-btn-ghost v-btn-sm text-xs py-1 px-3"
              >
                {t.common.cancel || "Cancelar"}
              </button>
            </div>
          </div>
        ) : (
          <button
            data-testid={`assistant-proposal-apply-all-${proposal.id}`}
            disabled={isFullyApplied || isDismissed || isScriptMismatch}
            onClick={requestApplyAll}
            className="v-btn-ghost v-btn-sm mt-2 w-full justify-center disabled:opacity-40"
          >
            {isFullyApplied ? t.assistant.appliedTag : t.assistant.applyAll}
          </button>
        )
      )}
    </div>
  );
};

const Proposal = ({ proposal, projectId, data, messageId }) => {
  if (!proposal) return null;

  if (proposal?.type === "update_scene_prompt" || (proposal?.sceneId && (!proposal?.scenes || proposal?.scenes.length === 0))) {
    return <PromptUpdateProposalCard proposal={proposal} projectId={projectId} data={data} messageId={messageId} />;
  }

  return <SceneCreationProposalCard proposal={proposal} projectId={projectId} data={data} messageId={messageId} />;
};

// Registro de streams activos por ID de proyecto con soporte para reconexión y sincronización
// de instancias que se remontan o actualizan durante la emisión.
export const activeProjectStreams = new Map();

/**
 * Aborta un AbortController asociando de manera compatible la razón de cancelación tanto a controller como a signal.
 */
export const abortControllerWithReason = (controller, reason) => {
  if (!controller) return;
  try {
    controller.signal.reason = reason;
  } catch { /* no-op */ }
  controller.reason = reason;
  try {
    controller.abort(reason);
  } catch {
    controller.abort();
  }
};

/**
 * Cancela y limpia todos los streams activos en el registro de proyectos (p. ej. al cerrar sesión).
 */
export const clearActiveProjectStreams = () => {
  for (const [pid, stream] of activeProjectStreams.entries()) {
    if (stream?.controller) {
      try {
        abortControllerWithReason(stream.controller, "logout");
      } catch { /* no-op */ }
    }
    if (stream?.listeners) {
      stream.listeners.forEach((listener) => {
        try {
          listener({ type: "cancelled", cancelData: { status: "cancelled", interrupted: false, action: "logout" } });
        } catch { /* no-op */ }
      });
      stream.listeners.clear();
    }
  }
  activeProjectStreams.clear();
};

const AssistantPanelInner = ({
  data,
  mode = "normal",
  onModeChange,
  width = 384,
  onWidthChange,
  onClose,
  isMobile = false,
  isWide = true,
  onNavigate,
}) => {
  const { dispatch, state, catalog } = useStudio();
  const { project, thread } = data;
  const [text, setText] = useState("");

  // Modelos y configuración dinámica
  const [models, setModels] = useState([]);
  const [loadingModels, setLoadingModels] = useState(true);
  const [modelsError, setModelsError] = useState(null);
  const [selectedModelKey, setSelectedModelKey] = useState("");
  const currentSelectedModel = models.find((m) => `${m.provider_id}:${m.model_id}` === selectedModelKey);

  // Estados de streaming en tiempo real
  const [isStreaming, setIsStreaming] = useState(false);
  const [canStop, setCanStop] = useState(false);
  const [currentDelta, setCurrentDelta] = useState("");
  const [currentProposals, setCurrentProposals] = useState([]);
  const [streamingError, setStreamingError] = useState(null);

  // Estados de alertas consultivas de continuidad
  const [continuityState, setContinuityState] = useState({
    status: "idle", // 'idle' | 'loading' | 'success' | 'error'
    projectId: null,
    observations: [],
    error: null,
  });
  const [missingSceneWarning, setMissingSceneWarning] = useState(null);
  const [pickingSceneForObsId, setPickingSceneForObsId] = useState(null);
  const [informativeModalOpen, setInformativeModalOpen] = useState(false);
  const [expandedPrompts, setExpandedPrompts] = useState({});

  // Jerarquía visual: observaciones que requieren atención (contradicciones y riesgos) vs notas informativas
  const attentionObservations = useMemo(() => {
    return (continuityState.observations || []).filter((obs) => obs.severity !== "info");
  }, [continuityState.observations]);

  const informativeObservations = useMemo(() => {
    return (continuityState.observations || []).filter((obs) => obs.severity === "info");
  }, [continuityState.observations]);

  // Estados de preparación de prompt vinculada a una toma seleccionada
  const [draftSceneContext, setDraftSceneContext] = useState(null); // { sceneId, sceneTitle, projectId }
  const [noSceneNotice, setNoSceneNotice] = useState(null);
  const [isScriptMode, setIsScriptMode] = useState(false);
  const [isScriptModalOpen, setIsScriptModalOpen] = useState(false);
  const [isPlanRequestModalOpen, setIsPlanRequestModalOpen] = useState(false);
  const [activePlanModalOpen, setActivePlanModalOpen] = useState(false);

  // Guion activo actual del proyecto
  const activeScript = data?.activeScript || (data?.scripts || []).find((sc) => sc.id === project?.activeScriptId || sc.isActive) || null;

  // Comprobación de vigencia del plan de producción activo
  const planValidity = useMemo(() => {
    if (!project?.activePlan) return { isOutdated: false, reasons: [] };
    return checkPlanValidity(project.activePlan, {
      project,
      scenes: data?.scenes || [],
      characters: data?.characters || [],
      references: data?.references || [],
      activeScript,
    });
  }, [project, data?.scenes, data?.characters, data?.references, activeScript]);

  const handleActivatePlan = useCallback((plan) => {
    if (!project?.id) return;
    const snapshot = computePlanSnapshot({
      project,
      scenes: data?.scenes || [],
      characters: data?.characters || [],
      references: data?.references || [],
      activeScript,
    });

    const activePlanEntity = {
      ...plan,
      id: plan.id || `plan_${Date.now()}`,
      projectId: project.id,
      snapshot,
      activatedAt: new Date().toISOString(),
    };

    dispatch({
      type: "PROJECT_SET_ACTIVE_PLAN",
      payload: {
        projectId: project.id,
        plan: activePlanEntity,
      },
    });
  }, [project, data?.scenes, data?.characters, data?.references, activeScript, dispatch]);

  const handleRequestChangesOnPlan = useCallback((plan, instructions) => {
    const changesPrompt = [
      `Solicitud de ajustes sobre el Plan de Producción «${plan.title || "Plan de Producción"}» (${plan.version || "v1"}):`,
      ``,
      `=== INSTRUCCIONES DEL REALIZADOR ===`,
      instructions,
      ``,
      `=== CONTEXTO DEL PLAN PREVIO ===`,
      plan.summary || (typeof plan.content === "string" ? plan.content.slice(0, 500) : ""),
    ].join("\n");

    setText(changesPrompt);
    if (textareaRef.current) {
      textareaRef.current.focus();
      setTimeout(() => {
        if (textareaRef.current) {
          textareaRef.current.selectionStart = textareaRef.current.value.length;
          textareaRef.current.selectionEnd = textareaRef.current.value.length;
        }
      }, 50);
    }
  }, []);

  const handleClearActivePlan = useCallback(() => {
    if (!project?.id) return;
    dispatch({
      type: "PROJECT_CLEAR_ACTIVE_PLAN",
      payload: {
        projectId: project.id,
      },
    });
  }, [project?.id, dispatch]);

  const handleRequestPlanUpdate = useCallback(() => {
    const reasonsText = planValidity.reasons.length > 0
      ? `\n\nCambios detectados desde la última versión:\n${planValidity.reasons.map((r) => `- ${r}`).join("\n")}`
      : "";

    const updatePrompt = buildProductionPlanPrompt({
      project,
      scenes: data?.scenes || [],
      characters: data?.characters || [],
      references: data?.references || [],
      scripts: data?.scripts || [],
      activeScript,
      assets: data?.assets || [],
      jobs: data?.jobs || [],
      catalog,
      budget: state?.onboarding?.budget,
      messages: thread?.messages || [],
    });

    const activeDisplay = project?.activePlan ? getCanonicalPlanDisplay(project.activePlan) : null;
    const activeHeader = activeDisplay ? `«${activeDisplay.displayTitle}» (${activeDisplay.canonicalVersion})` : "«Plan de Producción» (v1)";
    const fullPrompt = `Se requiere una actualización del Plan de Producción activo (${activeHeader}) debido a cambios en el proyecto.${reasonsText}\n\n${updatePrompt}`;
    setText(fullPrompt);
    if (textareaRef.current) {
      textareaRef.current.focus();
      setTimeout(() => {
        if (textareaRef.current) {
          textareaRef.current.selectionStart = textareaRef.current.value.length;
          textareaRef.current.selectionEnd = textareaRef.current.value.length;
        }
      }, 50);
    }
  }, [project, planValidity, data, activeScript, catalog, state?.onboarding?.budget, thread?.messages]);

  // Referencias de control y ciclo de vida
  const panelRef = useRef(null);
  const abortControllerRef = useRef(null);
  const previousProjectIdRef = useRef(project?.id);
  const activeProjectIdRef = useRef(project?.id);
  const isStreamingRef = useRef(false);
  const streamStartTimeRef = useRef(0);
  const textareaRef = useRef(null);
  const messagesEndRef = useRef(null);

  // 1. Cargar modelos configurados desde el backend (GET /api/assistant/models)
  useEffect(() => {
    let isMounted = true;
    setLoadingModels(true);
    setModelsError(null);

    Promise.resolve(api.getAssistantModels ? api.getAssistantModels() : [])
      .then((list) => {
        if (!isMounted) return;
        const available = Array.isArray(list) ? list : [];
        setModels(available);
        setLoadingModels(false);

        if (available.length > 0) {
          const savedKey = localStorage.getItem("veyra_assistant_selected_model");
          const found = available.find((m) => `${m.provider_id}:${m.model_id}` === savedKey);
          if (found) {
            setSelectedModelKey(savedKey);
          } else {
            const def = available.find((m) => m.is_default) || available[0];
            setSelectedModelKey(`${def.provider_id}:${def.model_id}`);
          }
        }
      })
      .catch((err) => {
        if (!isMounted) return;
        setLoadingModels(false);
        setModelsError(err.message || "Error al cargar modelos de IA.");
      });

    return () => {
      isMounted = false;
    };
  }, []);

  // 2. Control de aislamiento de proyecto, reconexión y sincronización en tiempo real
  useEffect(() => {
    const prevPid = previousProjectIdRef.current;
    const nextPid = project?.id;
    if (nextPid) {
      previousProjectIdRef.current = nextPid;
    }

    // Solo abortar y limpiar si el usuario efectivamente cambió a otro proyecto diferente
    if (prevPid && nextPid && prevPid !== nextPid) {
      const prevStream = activeProjectStreams.get(prevPid);
      if (prevStream?.controller) {
        abortControllerWithReason(prevStream.controller, "project_change");
        activeProjectStreams.delete(prevPid);
      }
      if (abortControllerRef.current) {
        abortControllerWithReason(abortControllerRef.current, "project_change");
        abortControllerRef.current = null;
      }
      isStreamingRef.current = false;
      setIsStreaming(false);
      setCanStop(false);
      setCurrentDelta("");
      setCurrentProposals([]);
      setStreamingError(null);
      setDraftSceneContext(null);
      setNoSceneNotice(null);
      setIsScriptMode(false);
      setMissingSceneWarning(null);
      setPickingSceneForObsId(null);
      setContinuityState({
        status: "idle",
        projectId: nextPid,
        observations: [],
        error: null,
      });
      activeProjectIdRef.current = nextPid;
      return;
    }

    activeProjectIdRef.current = nextPid;
    if (!nextPid) return;

    // Si el proyecto actual ya tiene un stream en curso (incluso tras desmontaje/remontaje o guardado automático),
    // restaurar el estado visible y suscribirse para recibir deltas, propuestas y finalización en tiempo real.
    const activeStream = activeProjectStreams.get(nextPid);
    if (activeStream && !activeStream.controller.signal.aborted) {
      abortControllerRef.current = activeStream.controller;
      isStreamingRef.current = true;
      streamStartTimeRef.current = activeStream.startTime || Date.now();
      setIsStreaming(true);
      setCanStop(true);
      setCurrentDelta(activeStream.accumulatedText || "");
      setCurrentProposals([...(activeStream.accumulatedProposals || [])]);

      const streamListener = (event) => {
        if (event.type === "delta") {
          setCurrentDelta(event.text);
        } else if (event.type === "proposal") {
          setCurrentProposals(event.proposals);
        } else if (event.type === "done" || event.type === "cancelled") {
          isStreamingRef.current = false;
          setIsStreaming(false);
          setCurrentDelta("");
          setCurrentProposals([]);
          abortControllerRef.current = null;
        } else if (event.type === "error") {
          isStreamingRef.current = false;
          setIsStreaming(false);
          setCurrentDelta("");
          setCurrentProposals([]);
          setStreamingError(event.error);
          abortControllerRef.current = null;
          if (event.savedText) setText(event.savedText);
          if (event.savedDraftSceneContext) setDraftSceneContext(event.savedDraftSceneContext);
        }
      };

      activeStream.listeners.add(streamListener);
      return () => {
        activeStream.listeners.delete(streamListener);
      };
    }
  }, [project?.id]);

  // Limpiar aviso de toma ausente cuando el usuario selecciona una toma
  useEffect(() => {
    if (data?.selection?.sceneId) {
      setNoSceneNotice(null);
    }
  }, [data?.selection?.sceneId]);

  // 3. Inspección consultiva de continuidad cinematográfica
  const triggerContinuityInspect = useCallback(async () => {
    const targetPid = project.id;
    setContinuityState({
      status: "loading",
      projectId: targetPid,
      observations: [],
      error: null,
    });

    try {
      let observations = null;
      if (api.inspectContinuity) {
        observations = await api.inspectContinuity(targetPid);
      }
      if (!Array.isArray(observations)) {
        observations = analyzeProjectContinuity({
          scenes: data.scenes || [],
          characters: data.characters || [],
        });
      }

      // Si el proyecto cambió durante la petición, descartar respuesta tardía
      if (activeProjectIdRef.current !== targetPid) {
        return;
      }

      setContinuityState({
        status: "success",
        projectId: targetPid,
        observations,
        error: null,
      });
    } catch (err) {
      if (activeProjectIdRef.current !== targetPid) {
        return;
      }
      setContinuityState({
        status: "error",
        projectId: targetPid,
        observations: [],
        error: err?.message || t.assistant.continuityError || "Error al inspeccionar continuidad.",
      });
    }
  }, [project.id, data.scenes, data.characters]);

  // Navegación hacia una toma afectada abriéndola en Storyboard y ajustando la presentación del Director
  const handleNavigateToScene = useCallback((sceneId, alertObs = null) => {
    if (!sceneId) return;

    // Validación de pertenencia: comprobar que la alerta corresponde al proyecto activo
    if (alertObs?.projectId && alertObs.projectId !== project.id) {
      return;
    }
    if (continuityState.projectId && continuityState.projectId !== project.id) {
      return;
    }

    // Validación de pertenencia: comprobar que la escena pertenece al proyecto activo
    const targetScene = (data.scenes || []).find((s) => s.id === sceneId);
    if (!targetScene) {
      // La escena ya no existe en el proyecto activo: mostrar aviso claro sin abrir otra como sustituto
      setMissingSceneWarning({
        sceneId,
        message: t.assistant?.sceneNotFoundInProject || "La toma seleccionada ya no existe en este proyecto.",
      });
      return;
    }

    setMissingSceneWarning(null);

    // 1. Selecciona la toma exacta en el store
    dispatch({
      type: "SELECT",
      payload: {
        projectId: project.id,
        patch: { sceneId: targetScene.id, assetId: targetScene.selectedAssetId },
      },
    });

    // 2. Abre el Storyboard del proyecto correspondiente mediante la navegación existente
    const targetPath = `/projects/${project.id}/storyboard`;
    if (typeof onNavigate === "function") {
      onNavigate(targetPath);
    } else if (typeof window !== "undefined" && window.location) {
      try {
        if (window.history && typeof window.history.pushState === "function") {
          window.history.pushState({}, "", targetPath);
          window.dispatchEvent(new PopStateEvent("popstate"));
        }
      } catch { /* no-op */ }
    }

    // 3. Si el Director está ampliado o a pantalla completa, vuelve automáticamente al modo lateral
    if (mode === "expanded" || mode === "fullscreen") {
      onModeChange?.("normal");
    }
    // Si ya está lateral ("normal"), conserva ese modo

    // 4. Distribución de paneles:
    // Conserva el Director lateral si hay espacio suficiente (escritorio amplio >= 1280px).
    // Si ambos paneles (Director lateral + Detalle de escena) dejan inutilizables las tarjetas
    // (en pantallas < 1280px como 1024px o móvil), repliega el Director utilizando el mecanismo existente (onClose)
    const shouldCollapseDirector = Boolean(
      isMobile ||
      (typeof window !== "undefined" && window.innerWidth < 1280)
    );
    if (shouldCollapseDirector) {
      onClose?.();
    }

    // 5. Desplazamiento suave al elemento de la escena en Storyboard
    try {
      const el = document.querySelector(`[data-testid="scene-card-${targetScene.id}"]`);
      if (el && typeof el.scrollIntoView === "function") {
        el.scrollIntoView({ behavior: "smooth", block: "nearest" });
      }
    } catch { /* no-op */ }
  }, [project.id, data.scenes, continuityState.projectId, dispatch, onNavigate, mode, onModeChange, isMobile, onClose]);

  // Desplazamiento automático al final
  const scrollToBottom = useCallback(() => {
    if (typeof messagesEndRef.current?.scrollIntoView === "function") {
      messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [thread?.messages?.length, currentDelta, scrollToBottom]);

  // Selección de modelo (sin iniciar solicitud automática)
  const handleModelSelect = (key) => {
    setSelectedModelKey(key);
    try {
      localStorage.setItem("veyra_assistant_selected_model", key);
    } catch {
      // ignore
    }
  };

  const currentSceneId = data?.selection?.sceneId || null;
  const selectedScene = currentSceneId ? (data?.scenes || []).find((s) => s.id === currentSceneId) : null;
  const isSceneMismatch = Boolean(
    draftSceneContext &&
    currentSceneId &&
    draftSceneContext.sceneId !== currentSceneId
  );

  // Construye la solicitud vinculada inequívocamente a la toma seleccionada y su contexto real
  const buildScenePromptDraft = useCallback(
    (scene) => {
      if (!scene) return "";
      const sceneTitle = scene.title?.trim() || (scene.order !== undefined ? `Toma ${scene.order + 1}` : scene.id);
      const sceneId = scene.id;
      const currentPrompt = scene.prompt?.trim() || null;
      const currentNotes = scene.notes?.trim() || null;
      const currentDuration = scene.duration ? `${scene.duration}s` : null;

      // Referencias asignadas a esa toma (referenceIds) dentro de las referencias del proyecto
      const sceneRefIds = Array.isArray(scene.referenceIds) ? scene.referenceIds : [];
      const assignedRefs = (data?.references || []).filter((r) => sceneRefIds.includes(r.id));

      // Personajes realmente asignados a esa toma (por referencia asignada, characterIds o @handle en prompt)
      const assignedChars = (data?.characters || []).filter((c) => {
        if (!c) return false;
        const cleanHandle = (c.handle || "").replace(/^@+/, "").trim();
        if (!cleanHandle) return false;
        const inRefs = assignedRefs.some((r) => r.characterId === c.id);
        const inCharIds = Array.isArray(scene.characterIds) && scene.characterIds.includes(c.id);
        const escaped = cleanHandle.replace(/[-/\\^$*+?.()|[\]{}]/g, "\\$&");
        const regex = new RegExp(`(?:^|[^a-zA-Z0-9_])@${escaped}(?![a-zA-Z0-9_])`, "i");
        const inPrompt = currentPrompt ? regex.test(currentPrompt) : false;
        return inRefs || inCharIds || inPrompt;
      });

      const assignedCharsList =
        assignedChars.length > 0
          ? assignedChars.map((c) => {
              const clean = (c.handle || "").replace(/^@+/, "").trim();
              return c.name ? `${c.name} (@${clean})` : `@${clean}`;
            }).join(", ")
          : "Ninguno asignado";

      const assignedRefsList =
        assignedRefs.length > 0
          ? assignedRefs.map((r) => r.label || r.id).join(", ")
          : assignedChars.length > 0
          ? "Sin referencia visual seleccionada"
          : "Ninguna asignada";

      return [
        `Actúa como Creative Director para preparar el prompt de producción de la siguiente toma:`,
        ``,
        `• Proyecto: ${project?.name || "Sin título"} (ID: ${project?.id || ""})`,
        `• Toma seleccionada: ${sceneTitle} (ID: ${sceneId})`,
        `• Duración: ${currentDuration || "No especificada"}`,
        `• Prompt actual: ${currentPrompt ? `"${currentPrompt}"` : "Sin prompt previo"}`,
        `• Notas de continuidad: ${currentNotes ? `"${currentNotes}"` : "Sin notas registradas"}`,
        `• Personajes asignados: ${assignedCharsList}`,
        `• Referencias visuales asignadas: ${assignedRefsList}`,
        ``,
        `Instrucción: Redacta un prompt cinematográfico detallado y optimizado para esta toma, especificando sujeto, encuadre de cámara, iluminación, paleta cromática y estilo visual en coherencia con el proyecto. Respeta estrictamente las notas de continuidad indicadas; si detectas posibles contradicciones entre las notas, el prompt previo o las referencias visuales, señálalas explícitamente sin resolverlas en silencio ni inventar detalles no documentados.`,
        ``,
        `Directrices de respuesta:`,
        `1. Diferencia estrictamente datos confirmados (la toma y las instrucciones explícitas) de sugerencias creativas e información no especificada. Una instrucción explícita del usuario (como «día lluvioso») ya es válida y definitiva para esta toma: no pidas confirmarla de nuevo; pregunta únicamente si propones cambiarla o extenderla a otras tomas.`,
        `2. Desarrolla detalles compatibles con la intención. Si propones cambiar una condición explícita (día/noche, acción, personaje, lugar, vestuario o resultado narrativo), explica brevemente el beneficio y pide confirmación antes de incorporarla al prompt final, conservando la condición original como vigente mientras no sea aceptada.`,
        `3. Conserva el estilo documentado del proyecto/personaje; si falta, sugiere el estilo como propuesta sin imponer realismo. Ratio y resolución solo son confirmados si constan en el contexto real.`,
        `4. Si faltan duración u otros parámetros, sugiérelos identificándolos como propuestas sin darlos por establecidos ni guardados. Muestra cualquier duración sugerida fuera del prompt y no introduzcas el prefijo «@Escena N [Xs]» dentro del prompt propuesto.`,
        `5. Considera como continuidad confirmada solo datos reales de tomas guardadas; las sugerencias anteriores no aceptadas siguen siendo propuestas.`,
        `6. Mantén un lenguaje sencillo y una respuesta proporcionada, sin exigir tecnicismos al realizador ni afirmar que examinaste imágenes reales.`,
        `7. Para actualizar esta toma sin duplicarla, emite tu propuesta estructurada con la etiqueta <veyra_proposal> de tipo "update_scene_prompt" vinculada a projectId "${project?.id}" y sceneId "${sceneId}".`,
      ].join("\n");
    },
    [project?.name, project?.id, data?.references, data?.characters]
  );

  // Construye la solicitud de mejora cinematográfica vinculando el contexto real de la toma y la petición del usuario
  const buildSceneImprovePrompt = useCallback(
    (scene, userImprovement) => {
      if (!scene) return userImprovement || "";
      const sceneTitle = scene.title?.trim() || (scene.order !== undefined ? `Toma ${scene.order + 1}` : scene.id);
      const sceneId = scene.id;
      const currentPrompt = scene.prompt?.trim() || null;
      const currentNotes = scene.notes?.trim() || null;
      const currentDuration = scene.duration ? `${scene.duration}s` : null;

      // Referencias asignadas a esa toma (referenceIds) dentro de las referencias del proyecto
      const sceneRefIds = Array.isArray(scene.referenceIds) ? scene.referenceIds : [];
      const assignedRefs = (data?.references || []).filter((r) => sceneRefIds.includes(r.id));

      // Personajes realmente asignados a esa toma (por referencia asignada, characterIds o @handle en prompt)
      const assignedChars = (data?.characters || []).filter((c) => {
        if (!c) return false;
        const cleanHandle = (c.handle || "").replace(/^@+/, "").trim();
        if (!cleanHandle) return false;
        const inRefs = assignedRefs.some((r) => r.characterId === c.id);
        const inCharIds = Array.isArray(scene.characterIds) && scene.characterIds.includes(c.id);
        const escaped = cleanHandle.replace(/[-/\\^$*+?.()|[\]{}]/g, "\\$&");
        const regex = new RegExp(`(?:^|[^a-zA-Z0-9_])@${escaped}(?![a-zA-Z0-9_])`, "i");
        const inPrompt = currentPrompt ? regex.test(currentPrompt) : false;
        return inRefs || inCharIds || inPrompt;
      });

      const assignedCharsList =
        assignedChars.length > 0
          ? assignedChars.map((c) => {
              const clean = (c.handle || "").replace(/^@+/, "").trim();
              return c.name ? `${c.name} (@${clean})` : `@${clean}`;
            }).join(", ")
          : "Ninguno asignado";

      const assignedRefsList =
        assignedRefs.length > 0
          ? assignedRefs.map((r) => r.label || r.id).join(", ")
          : assignedChars.length > 0
          ? "Sin referencia visual seleccionada"
          : "Ninguna asignada";

      return [
        `Actúa como Creative Director para realizar una mejora cinematográfica sobre la siguiente toma:`,
        ``,
        `• Proyecto: ${project?.name || "Sin título"} (ID: ${project?.id || ""})`,
        `• Toma seleccionada: ${sceneTitle} (ID: ${sceneId})`,
        `• Duración: ${currentDuration || "No especificada"}`,
        `• Prompt actual: ${currentPrompt ? `"${currentPrompt}"` : "Sin prompt previo"}`,
        `• Notas de continuidad: ${currentNotes ? `"${currentNotes}"` : "Sin notas registradas"}`,
        `• Personajes asignados: ${assignedCharsList}`,
        `• Referencias visuales asignadas: ${assignedRefsList}`,
        ``,
        `Mejora solicitada por el realizador:`,
        `"${userImprovement || ""}"`,
        ``,
        `Instrucciones de respuesta y mejora:`,
        `1. Aplica la mejora solicitada incorporándola con coherencia cinematográfica al prompt de esta toma y conservando todos los demás elementos confirmados (personajes, notas, vestuario y ambientación).`,
        `2. No reinterpretes toda la escena desde cero ni cambies la condición temporal (día/noche), la acción principal, la identidad de los personajes ni el estilo visual sin aprobación previa del realizador.`,
        `3. Si la petición es ambigua o presenta contradicciones con la toma, formula una pregunta breve antes de asumir cambios no documentados.`,
        `4. No afirmes haber examinado o visto imágenes reales; dispones únicamente de la información y metadatos textuales proporcionados.`,
        `5. Explica brevemente qué cambios propones y las razones cinematográficas de la optimización.`,
        `6. Para actualizar esta toma sin duplicarla, emite tu propuesta estructurada con la etiqueta <veyra_proposal> de tipo "update_scene_prompt" vinculada a projectId "${project?.id}" y sceneId "${sceneId}".`,
      ].join("\n");
    },
    [project?.name, project?.id, data?.references, data?.characters]
  );

  // Construye la solicitud de guion cinematográfico a partir de la intención narrativa del realizador
  const buildScriptDraftPrompt = useCallback(
    (userIdea) => {
      const activeProjectName = project?.name?.trim() || "Proyecto Sin Título";
      const activeProjectId = project?.id || "";
      const linkedIds = new Set(project?.linkedResourceIds || []);

      // Filtrar escenas autorizadas del proyecto activo
      const projectScenes = (data?.scenes || []).filter(
        (s) => !s.projectId || s.projectId === activeProjectId
      );

      // Filtrar personajes autorizados del proyecto activo (aislamiento estricto)
      const projectChars = (data?.characters || []).filter((c) => {
        if (!c) return false;
        if (!c.projectId) return true;
        if (c.projectId === activeProjectId) return true;
        if (c.scope === "global" && linkedIds.has(c.id)) return true;
        if (activeProjectId === "prj_demo01" && c.isDemo) return true;
        return false;
      });

      // Filtrar referencias autorizadas del proyecto activo (aislamiento estricto)
      const projectRefs = (data?.references || []).filter((r) => {
        if (!r) return false;
        if (r.projectId === activeProjectId) return true;
        if (r.scope === "global" && (linkedIds.has(r.id) || (r.characterId && linkedIds.has(r.characterId)))) return true;
        if (r.characterId && projectChars.some((c) => c.id === r.characterId)) return true;
        if (activeProjectId === "prj_demo01" && r.isDemo && !r.projectId) return true;
        return false;
      });

      // Lista formateada de personajes autorizados
      const charsList =
        projectChars.length > 0
          ? projectChars
              .map((c) => {
                const cleanHandle = (c.handle || "").replace(/^@+/, "").trim();
                const charDesc = c.description ? ` (${c.description})` : "";
                return c.name ? `${c.name} (@${cleanHandle})${charDesc}` : `@${cleanHandle}${charDesc}`;
              })
              .join("; ")
          : "Sin personajes definidos";

      // Lista formateada de referencias autorizadas
      const refsList =
        projectRefs.length > 0
          ? projectRefs.map((r) => r.label || r.id).join(", ")
          : "Sin referencias registradas";

      // Tomas guardadas en el storyboard
      const scenesList =
        projectScenes.length > 0
          ? projectScenes
              .map((s) => {
                const title = s.title?.trim() || (s.order !== undefined ? `Toma ${s.order + 1}` : s.id);
                const p = s.prompt?.trim() || "Sin prompt";
                return `• ${title}: "${p}"`;
              })
              .join("\n")
          : "No hay tomas guardadas en el storyboard.";

      const documentedStyle =
        project?.style ||
        project?.documentedStyle ||
        data?.workspacePrefs?.style ||
        "No documentado (presentar propuestas como sugerencias revisables sin imponer realismo por defecto)";

      const workspaceFormat = data?.workspacePrefs?.format || project?.format || "16:9";

      return [
        `Actúa como Creative Director para crear un borrador de guion a partir de la intención narrativa del realizador:`,
        ``,
        `• Proyecto activo: ${activeProjectName} (ID: ${activeProjectId})`,
        `• Estilo documentado: ${documentedStyle}`,
        `• Formato: ${workspaceFormat}`,
        `• Personajes autorizados: ${charsList}`,
        `• Referencias visuales autorizadas: ${refsList}`,
        `• Tomas guardadas existentes en storyboard:`,
        scenesList,
        ``,
        `Intención narrativa expresada por el realizador:`,
        `"${userIdea}"`,
        ``,
        `Directivas obligatorias de creación de guion:`,
        `1. Prioridad narrativa: La idea del realizador es prioritaria. Los personajes, referencias y tomas guardadas aportan contexto pero no sustituyen su intención narrativa.`,
        `2. Veracidad visual: No afirmes haber examinado o inspeccionado visualmente imágenes o planos; el sistema solo procesa nombres y metadatos textuales.`,
        `3. Solicitud de ideas: Si el realizador pide ideas o sugerencias (ej. "propónme ideas", "no sé qué contar"), ofrece de 2 a 3 opciones variadas para que elija antes de redactar un guion.`,
        `4. Información esencial vs. suficiente:`,
        `   - Si falta información ESENCIAL para estructurar la historia, formula una pregunta breve antes de redactar.`,
        `   - NO conviertas duración, formato técnico, diálogos ni tecnicismos cinematográficos en requisitos obligatorios.`,
        `   - Si la idea ya es suficiente, redacta el borrador directamente sin demoras ni cuestionarios innecesarios.`,
        `5. Estructura del guion: Presenta un guion claro y proporcionado con inicio (qué sucede), desarrollo (cómo avanza) y desenlace (cómo termina). Diálogos solo cuando correspondan a la historia.`,
        `6. Preservación y propuestas: Conserva todas las decisiones explícitas del realizador y presenta cualquier detalle nuevo o añadido como propuesta revisable en el chat.`,
        `7. Tomas existentes: Si el proyecto ya cuenta con tomas guardadas y la intención del realizador es ambigua respecto a si busca desarrollar la historia alrededor de las tomas existentes o explorar una alternativa, formula una pregunta breve aclaratoria. En ningún caso reemplaces prompts existentes, borres tomas ni crees escenas en el storyboard al redactar el guion.`,
        `8. Alcance del borrador: Limita el trabajo al flujo de crear y revisar el borrador como texto en el chat. No utilices etiquetas <veyra_proposal> de creación de escenas en el storyboard ni presentes el borrador como aprobado o guardado formalmente.`,
      ].join("\n");
    },
    [project?.name, project?.id, project?.linkedResourceIds, project?.style, project?.documentedStyle, project?.format, data?.scenes, data?.characters, data?.references, data?.workspacePrefs]
  );

  // Construye el borrador editable que el realizador verá y podrá editar en el chat existente
  const buildContinuityFixDraft = useCallback((scene, observation) => {
    const title = observation?.title || "Inconsistencia de continuidad";
    const desc = observation?.description || "";
    const rec = observation?.recommendation || "";
    const ev = observation?.evidence || {};
    let evSnippet = "";
    if (ev.canon_text || ev.canonText) {
      evSnippet += `\n• Canon establecido: "${ev.canon_text || ev.canonText}"`;
    }
    if (ev.scene_text || ev.sceneText) {
      evSnippet += `\n• Texto actual en toma: "${ev.scene_text || ev.sceneText}"`;
    }
    if (ev.prev_scene_text || ev.prevSceneText) {
      evSnippet += `\n• Toma previa: "${ev.prev_scene_text || ev.prevSceneText}"`;
    }

    return [
      `Por favor corrige la siguiente observación de continuidad en esta toma:`,
      `• Observación: ${title}`,
      `• Detalle: ${desc}${evSnippet}`,
      rec ? `• Recomendación: ${rec}` : null,
      ``,
      `Modifica únicamente el prompt de esta toma para resolver la inconsistencia y asegurar el raccord, conservando todos los demás elementos de la escena.`,
    ]
      .filter(Boolean)
      .join("\n");
  }, []);

  // Construye la solicitud técnica detallada para el Creative Director
  const buildSceneContinuityFixPrompt = useCallback(
    (scene, userInstruction, observation) => {
      if (!scene) return userInstruction || "";
      const sceneTitle = scene.title?.trim() || (scene.order !== undefined ? `Toma ${scene.order + 1}` : scene.id);
      const sceneId = scene.id;
      const currentPrompt = scene.prompt?.trim() || null;
      const currentNotes = scene.notes?.trim() || null;
      const currentDuration = scene.duration ? `${scene.duration}s` : null;

      const sceneRefIds = Array.isArray(scene.referenceIds) ? scene.referenceIds : [];
      const assignedRefs = (data?.references || []).filter((r) => sceneRefIds.includes(r.id));

      const assignedChars = (data?.characters || []).filter((c) => {
        if (!c) return false;
        const cleanHandle = (c.handle || "").replace(/^@+/, "").trim();
        if (!cleanHandle) return false;
        const inRefs = assignedRefs.some((r) => r.characterId === c.id);
        const inCharIds = Array.isArray(scene.characterIds) && scene.characterIds.includes(c.id);
        const escaped = cleanHandle.replace(/[-/\\^$*+?.()|[\]{}]/g, "\\$&");
        const regex = new RegExp(`(?:^|[^a-zA-Z0-9_])@${escaped}(?![a-zA-Z0-9_])`, "i");
        const inPrompt = currentPrompt ? regex.test(currentPrompt) : false;
        return inRefs || inCharIds || inPrompt;
      });

      const assignedCharsList =
        assignedChars.length > 0
          ? assignedChars.map((c) => {
              const clean = (c.handle || "").replace(/^@+/, "").trim();
              return c.name ? `${c.name} (@${clean})` : `@${clean}`;
            }).join(", ")
          : "Ninguno asignado";

      const assignedRefsList =
        assignedRefs.length > 0
          ? assignedRefs.map((r) => r.label || r.id).join(", ")
          : assignedChars.length > 0
          ? "Sin referencia visual seleccionada"
          : "Ninguna asignada";

      const ev = observation?.evidence || {};
      let evDetails = "";
      if (ev.canon_text || ev.canonText) {
        evDetails += `\n  - Canon: "${ev.canon_text || ev.canonText}"`;
      }
      if (ev.scene_text || ev.sceneText) {
        evDetails += `\n  - Texto en toma: "${ev.scene_text || ev.sceneText}"`;
      }
      if (ev.prev_scene_text || ev.prevSceneText) {
        evDetails += `\n  - Toma previa: "${ev.prev_scene_text || ev.prevSceneText}"`;
      }

      return [
        `Actúa como Creative Director para corregir una observación de continuidad sobre la siguiente toma:`,
        ``,
        `• Proyecto: ${project?.name || "Sin título"} (ID: ${project?.id || ""})`,
        `• Toma seleccionada: ${sceneTitle} (ID: ${sceneId})`,
        `• Duración: ${currentDuration || "No especificada"}`,
        `• Prompt actual: ${currentPrompt ? `"${currentPrompt}"` : "Sin prompt previo"}`,
        `• Notas de continuidad: ${currentNotes ? `"${currentNotes}"` : "Sin notas registradas"}`,
        `• Personajes asignados: ${assignedCharsList}`,
        `• Referencias visuales asignadas: ${assignedRefsList}`,
        ``,
        `Observación de continuidad detectada:`,
        `• Título: ${observation?.title || "Inconsistencia de continuidad"}`,
        `• Descripción: ${observation?.description || "Inconsistencia de raccord"}`,
        evDetails ? `• Evidencia detectada:${evDetails}` : null,
        observation?.recommendation ? `• Recomendación: ${observation.recommendation}` : null,
        ``,
        `Instrucción del realizador:`,
        `"${userInstruction || ""}"`,
        ``,
        `Instrucciones de corrección de continuidad:`,
        `1. Propón un prompt corregido que resuelva directamente la inconsistencia de continuidad indicada para esta toma.`,
        `2. Conserva estrictamente todos los demás elementos de la toma (acción, personajes asignados, estilo visual y notas) que no formen parte del conflicto.`,
        `3. No reinterpretes toda la escena ni cambies temporalidad, identidad o estilo sin indicación expresa.`,
        `4. No afirmes haber examinado o inspeccionado imágenes reales; dispones únicamente de la información y metadatos textuales proporcionados.`,
        `5. Explica brevemente qué cambiaste para resolver el raccord.`,
        `6. Emite tu propuesta estructurada con la etiqueta <veyra_proposal> de tipo "update_scene_prompt" vinculada a projectId "${project?.id}" y sceneId "${sceneId}".`,
      ].filter(Boolean).join("\n");
    },
    [project?.name, project?.id, data?.references, data?.characters]
  );

  const handleRequestContinuityFix = useCallback(
    (obs, explicitSceneId = null) => {
      if (!obs) return;

      const affectedIds = Array.isArray(obs.affected_scene_ids)
        ? obs.affected_scene_ids
        : Array.isArray(obs.affectedSceneIds)
        ? obs.affectedSceneIds
        : [];

      let chosenSceneId = explicitSceneId;

      if (!chosenSceneId) {
        if (affectedIds.length === 1) {
          chosenSceneId = affectedIds[0];
        } else if (affectedIds.length > 1) {
          setPickingSceneForObsId(obs.id);
          return;
        } else {
          chosenSceneId = data?.selection?.sceneId || null;
        }
      }

      setPickingSceneForObsId(null);

      if (!chosenSceneId) {
        setNoSceneNotice(t.assistant?.selectSceneForPrompt || "Selecciona una toma del Storyboard");
        return;
      }

      const targetScene = (data?.scenes || []).find((s) => s.id === chosenSceneId);
      if (!targetScene) {
        setMissingSceneWarning({
          sceneId: chosenSceneId,
          message: t.assistant?.sceneNotFoundInProject || "La toma seleccionada ya no existe en este proyecto.",
        });
        return;
      }

      // 1. Selecciona la toma en el store
      dispatch({
        type: "SELECT",
        payload: {
          projectId: project.id,
          patch: { sceneId: targetScene.id, assetId: targetScene.selectedAssetId },
        },
      });

      // 2. Vincula el borrador
      const sceneTitle = targetScene.title?.trim() || (targetScene.order !== undefined ? `Toma ${targetScene.order + 1}` : targetScene.id);
      setDraftSceneContext({
        sceneId: targetScene.id,
        sceneTitle,
        projectId: project.id,
        mode: "continuity",
        observation: obs,
        observationId: obs.id,
        observationTitle: obs.title,
      });

      // 3. Prepara el texto editable
      const draft = buildContinuityFixDraft(targetScene, obs);
      setText(draft);
      setNoSceneNotice(null);

      // 4. Enfoca el campo de texto
      if (textareaRef.current) {
        textareaRef.current.focus();
        setTimeout(() => {
          if (textareaRef.current) {
            textareaRef.current.selectionStart = textareaRef.current.value.length;
            textareaRef.current.selectionEnd = textareaRef.current.value.length;
          }
        }, 50);
      }
    },
    [data?.scenes, data?.selection?.sceneId, dispatch, project?.id, buildContinuityFixDraft]
  );

  useEffect(() => {
    const handleExternalRequestFix = (e) => {
      const { observation, sceneId } = e.detail || {};
      if (observation) {
        handleRequestContinuityFix(observation, sceneId);
      }
    };
    window.addEventListener("veyra:continuity:request-fix", handleExternalRequestFix);
    return () => {
      window.removeEventListener("veyra:continuity:request-fix", handleExternalRequestFix);
    };
  }, [handleRequestContinuityFix]);

  const handleUpdateDraft = useCallback(() => {
    if (isStreaming) return;
    const currentSceneId = data?.selection?.sceneId || null;
    const targetScene = currentSceneId ? (data?.scenes || []).find((s) => s.id === currentSceneId) : null;

    if (!targetScene) {
      setNoSceneNotice(
        draftSceneContext?.mode === "improve"
          ? (t.assistant?.selectSceneForImprove || "Selecciona una toma del Storyboard para mejorar su escena")
          : (t.assistant?.selectSceneForPrompt || "Selecciona una toma del Storyboard para preparar su prompt")
      );
      return;
    }

    setNoSceneNotice(null);
    const sceneTitle = targetScene.title?.trim() || (targetScene.order !== undefined ? `Toma ${targetScene.order + 1}` : targetScene.id);
    if (draftSceneContext?.mode === "improve") {
      setDraftSceneContext({
        sceneId: targetScene.id,
        sceneTitle,
        projectId: project.id,
        mode: "improve",
      });
    } else if (draftSceneContext?.mode === "continuity") {
      setDraftSceneContext({
        sceneId: targetScene.id,
        sceneTitle,
        projectId: project.id,
        mode: "continuity",
        observation: draftSceneContext.observation,
        observationId: draftSceneContext.observationId,
        observationTitle: draftSceneContext.observationTitle,
      });
      if (draftSceneContext.observation) {
        const draft = buildContinuityFixDraft(targetScene, draftSceneContext.observation);
        setText(draft);
      }
    } else {
      const draft = buildScenePromptDraft(targetScene);
      setText(draft);
      setDraftSceneContext({
        sceneId: targetScene.id,
        sceneTitle,
        projectId: project.id,
        mode: "prompt",
      });
    }
  }, [isStreaming, data?.selection?.sceneId, data?.scenes, buildScenePromptDraft, buildContinuityFixDraft, draftSceneContext, project?.id]);

  const handleQuickAction = (key, promptText) => {
    if (key === "continuity") {
      setIsScriptMode(false);
      triggerContinuityInspect();
      return;
    }
    if (isStreaming) return;

    if (key === "script") {
      setNoSceneNotice(null);
      setDraftSceneContext(null);
      setIsScriptMode(true);
      setText("");
      if (textareaRef.current) {
        textareaRef.current.focus();
      }
      return;
    }

    setIsScriptMode(false);

    if (key === "prompt") {
      const currentSceneId = data?.selection?.sceneId || null;
      const targetScene = currentSceneId ? (data?.scenes || []).find((s) => s.id === currentSceneId) : null;

      if (!targetScene) {
        setNoSceneNotice(t.assistant?.selectSceneForPrompt || "Selecciona una toma del Storyboard para preparar su prompt");
        return;
      }

      setNoSceneNotice(null);
      const sceneTitle = targetScene.title?.trim() || (targetScene.order !== undefined ? `Toma ${targetScene.order + 1}` : targetScene.id);
      const draft = buildScenePromptDraft(targetScene);
      setText(draft);
      setDraftSceneContext({
        sceneId: targetScene.id,
        sceneTitle,
        projectId: project.id,
        mode: "prompt",
      });

      if (textareaRef.current) {
        textareaRef.current.focus();
        setTimeout(() => {
          if (textareaRef.current) {
            textareaRef.current.selectionStart = textareaRef.current.value.length;
            textareaRef.current.selectionEnd = textareaRef.current.value.length;
          }
        }, 50);
      }
      return;
    }

    if (key === "improve") {
      const currentSceneId = data?.selection?.sceneId || null;
      const targetScene = currentSceneId ? (data?.scenes || []).find((s) => s.id === currentSceneId) : null;

      if (!targetScene) {
        setNoSceneNotice(t.assistant?.selectSceneForImprove || "Selecciona una toma del Storyboard para mejorar su escena");
        return;
      }

      setNoSceneNotice(null);
      const sceneTitle = targetScene.title?.trim() || (targetScene.order !== undefined ? `Toma ${targetScene.order + 1}` : targetScene.id);
      setDraftSceneContext({
        sceneId: targetScene.id,
        sceneTitle,
        projectId: project.id,
        mode: "improve",
      });

      if (textareaRef.current) {
        textareaRef.current.focus();
      }
      return;
    }

    if (key === "storyboard") {
      setNoSceneNotice(null);
      setDraftSceneContext(null);
      if (!activeScript) {
        setIsScriptModalOpen(true);
        return;
      }

      // Preparar solicitud revisable completa con el guion activo y las tomas reales del proyecto
      const projectScenes = (data?.scenes || []).filter((s) => s.projectId === project.id).sort((a, b) => a.order - b.order);
      const scenesDescription = projectScenes.length > 0
        ? projectScenes.map((s, idx) => `• Toma ${idx + 1} («${s.title || 'Sin título'}», ${s.duration || 3}s): ${s.prompt || 'Sin prompt'}`).join("\n")
        : "(Aún no hay tomas creadas en el proyecto)";

      const draftPrompt = `Estructura el Storyboard cinematográfico para el guion activo:

=== GUION ACTIVO: «${activeScript.title}» (${activeScript.versionId || 'v1'}) ===
${activeScript.content}

=== TOMAS EXISTENTES EN EL PROYECTO (${projectScenes.length} tomas) ===
${scenesDescription}

=== CRITERIOS DE DIRECCIÓN CINEMATOGRÁFICA ===
1. Propón las tomas necesarias para plasmar este guion con ritmo y coherencia visual, sin limitarte a una cantidad fija.
2. Para cada toma, define: acción principal, duración sugerida en segundos, plano y movimiento de cámara, referencias necesarias y sonidos previstos.
3. Si una toma existente del proyecto ya cumple la acción requerida, márcala explícitamente para REUTILIZAR (con su título e ID) sin crear una copia ni alterar su prompt original.
4. Si se requieren referencias de personajes o entornos no disponibles en el proyecto, enuméralas con precisión como referencias faltantes sin inventar asignaciones.
5. Vincula la propuesta a la versión ${activeScript.versionId || 'v1'} de «${activeScript.title}».`;

      setText(draftPrompt);
      if (textareaRef.current) {
        textareaRef.current.focus();
        setTimeout(() => {
          if (textareaRef.current) {
            textareaRef.current.selectionStart = textareaRef.current.value.length;
            textareaRef.current.selectionEnd = textareaRef.current.value.length;
          }
        }, 50);
      }
      return;
    }

    if (key === "plan") {
      setNoSceneNotice(null);
      setDraftSceneContext(null);

      const planPrompt = buildProductionPlanPrompt({
        project,
        scenes: data?.scenes || [],
        characters: data?.characters || [],
        references: data?.references || [],
        scripts: data?.scripts || [],
        activeScript,
        assets: data?.assets || [],
        jobs: data?.jobs || [],
        catalog,
        budget: state?.onboarding?.budget,
        messages: thread?.messages || [],
      });

      setText(planPrompt);
      if (textareaRef.current) {
        textareaRef.current.focus();
        setTimeout(() => {
          if (textareaRef.current) {
            textareaRef.current.selectionStart = textareaRef.current.value.length;
            textareaRef.current.selectionEnd = textareaRef.current.value.length;
          }
        }, 50);
      }
      return;
    }

    setText(promptText);
    if (textareaRef.current) {
      textareaRef.current.focus();
      setTimeout(() => {
        if (textareaRef.current) {
          textareaRef.current.selectionStart = textareaRef.current.value.length;
          textareaRef.current.selectionEnd = textareaRef.current.value.length;
        }
      }, 50);
    }
  };

  // Botón "Detener respuesta" con guarda anti-rebote post-envío
  const handleStop = (e) => {
    if (e) {
      e.preventDefault?.();
      e.stopPropagation?.();
    }
    // Protección contra doble pulsación accidental / rebote de evento al pulsar Enviar
    const elapsed = Date.now() - (streamStartTimeRef.current || 0);
    if (!canStop && elapsed < 450) {
      console.info("[CreativeDirector] Clic en Detener ignorado durante guarda post-envío:", { elapsedMs: elapsed });
      return;
    }
    isStreamingRef.current = false;
    setCanStop(false);
    const activeStream = activeProjectStreams.get(project?.id);
    if (activeStream?.controller) {
      abortControllerWithReason(activeStream.controller, "stop");
      activeProjectStreams.delete(project?.id);
    }
    if (abortControllerRef.current) {
      abortControllerWithReason(abortControllerRef.current, "stop");
      abortControllerRef.current = null;
    }
  };

  // Acciones de copia para respuestas del asistente
  const [copiedState, setCopiedState] = useState(null); // { id: string, type: 'simple' | 'context' }

  const handleCopy = useCallback(async (contentToCopy, messageId, type) => {
    if (!contentToCopy) return;
    let ok = false;
    try {
      if (navigator.clipboard && typeof navigator.clipboard.writeText === "function") {
        await navigator.clipboard.writeText(contentToCopy);
        ok = true;
      }
    } catch {
      // Fallback si la API de Clipboard del navegador no tiene permisos
    }
    if (!ok) {
      try {
        const textArea = document.createElement("textarea");
        textArea.value = contentToCopy;
        textArea.style.position = "fixed";
        textArea.style.left = "-999999px";
        textArea.style.top = "-999999px";
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();
        ok = document.execCommand("copy");
        document.body.removeChild(textArea);
      } catch (err) {
        console.error("Fallback copy failed:", err);
      }
    }
    if (ok) {
      setCopiedState({ id: messageId, type });
      setTimeout(() => {
        setCopiedState((prev) => (prev?.id === messageId && prev?.type === type ? null : prev));
      }, 2000);
    }
  }, []);

  const getMessageContextText = useCallback((msg) => {
    const projectName = project?.name || "Proyecto Sin Título";
    const modelInfo = [msg.provider, msg.model].filter(Boolean).join(" · ") || (currentSelectedModel ? `${currentSelectedModel.provider_label} (${currentSelectedModel.model_label})` : "Creative Director");
    return `Proyecto: ${projectName}\nModelo: ${modelInfo}\n---\n${msg.content || ""}`;
  }, [project?.name, currentSelectedModel]);

  // Envío e inicio de streaming SSE
  const send = async () => {
    if (isSceneMismatch || isStreamingRef.current || isStreaming) return;
    const content = text.trim();
    if (!content) return;

    // Preservar copia del borrador y contexto de escena para restaurarlo en caso de error
    const savedText = text;
    const savedDraftSceneContext = draftSceneContext;
    const savedIsScriptMode = isScriptMode;

    // Resolver mensaje para inferencia: si está vinculado en modo improve, continuity o script, ensamblar el prompt con el contexto real
    const isImproveMode = savedDraftSceneContext?.mode === "improve";
    const isContinuityMode = savedDraftSceneContext?.mode === "continuity";
    const isScriptModeDraft = savedIsScriptMode;
    const targetSceneForDraft = (isImproveMode || isContinuityMode) && savedDraftSceneContext?.sceneId
      ? (data?.scenes || []).find((s) => s.id === savedDraftSceneContext.sceneId)
      : null;

    const currentProjectId = project?.id;
    let outboundUserMessage = content;
    if (isImproveMode && targetSceneForDraft) {
      outboundUserMessage = buildSceneImprovePrompt(targetSceneForDraft, content);
    } else if (isContinuityMode && targetSceneForDraft) {
      outboundUserMessage = buildSceneContinuityFixPrompt(
        targetSceneForDraft,
        content,
        savedDraftSceneContext?.observation
      );
    } else if (isScriptModeDraft) {
      outboundUserMessage = buildScriptDraftPrompt(content);
    } else if (isPlanCorrectionRequest(content)) {
      // Localizar plan en revisión (plan activo o último plan propuesto en el hilo; sin activarlo automáticamente)
      const targetPlan = project?.activePlan || findLatestPlanInMessages(thread?.messages || [], currentProjectId);
      if (targetPlan) {
        outboundUserMessage = buildPlanCorrectionPrompt({
          userMessage: content,
          plan: targetPlan,
          project,
          scenes: data?.scenes || [],
          characters: data?.characters || [],
          references: data?.references || [],
          scripts: data?.scripts || [],
          activeScript,
          assets: data?.assets || [],
          jobs: data?.jobs || [],
          catalog,
          budget: state?.onboarding?.budget,
          messages: thread?.messages || [],
        });
      }
    }

    // Validación pre-vuelo de longitud del payload para proteger el endpoint y evitar 422
    const MAX_PAYLOAD_USER_MESSAGE = 15000;
    if (outboundUserMessage.length > MAX_PAYLOAD_USER_MESSAGE) {
      const excess = outboundUserMessage.length - MAX_PAYLOAD_USER_MESSAGE;
      const lengthNotice = `La solicitud supera el límite permitido (${outboundUserMessage.length.toLocaleString()} de ${MAX_PAYLOAD_USER_MESSAGE.toLocaleString()} caracteres; exceso: ${excess.toLocaleString()}). Se conservó tu borrador en el editor para que puedas ajustarlo.`;
      setStreamingError(lengthNotice);
      // Borrador y estado preservados íntegramente; no se despacha al hilo del chat
      return;
    }

    isStreamingRef.current = true;
    streamStartTimeRef.current = Date.now();

    setDraftSceneContext(null);
    setNoSceneNotice(null);
    setIsScriptMode(false);

    // 1. Registrar mensaje del usuario en el hilo del proyecto
    dispatch({
      type: "THREAD_MESSAGE",
      payload: { projectId: currentProjectId, message: { role: "user", content } },
    });
    setText("");
    setStreamingError(null);

    // Si no hay modelos activos configurados, emitir respuesta informativa
    if (models.length === 0) {
      dispatch({
        type: "THREAD_MESSAGE",
        payload: {
          projectId: currentProjectId,
          message: {
            role: "assistant",
            content: t.assistant.statusPendingDesc || "No hay modelos LLM activos configurados en el Panel Administrativo. Configura un proveedor con API key en Conexiones.",
            isDemo: false,
            system: true,
          },
        },
      });
      return;
    }

    // 2. Resolver modelo seleccionado
    const selectedModel = models.find((m) => `${m.provider_id}:${m.model_id}` === selectedModelKey) || models[0];

    // 3. Preparar historial para contexto cinematográfico (recortando defensivamente a 10000 caracteres)
    const history = (thread.messages || [])
      .slice(-10)
      .map((m) => ({
        role: m.role === "assistant" ? "assistant" : "user",
        content: typeof m.content === "string" ? m.content.slice(0, 10000) : "",
      }));

    // 4. Iniciar streaming con AbortController y registro con listeners
    const controller = new AbortController();
    abortControllerRef.current = controller;

    let accumulatedText = "";
    const accumulatedProposals = [];
    let capturedUsage = null;
    let capturedProvider = selectedModel.provider_label;
    let capturedModel = selectedModel.model_label || selectedModel.model_id;

    const streamEntry = {
      controller,
      startTime: streamStartTimeRef.current,
      accumulatedText: "",
      accumulatedProposals: [],
      capturedUsage: null,
      capturedProvider,
      capturedModel,
      savedText,
      savedDraftSceneContext,
      savedIsScriptMode,
      listeners: new Set(),
    };
    activeProjectStreams.set(currentProjectId, streamEntry);

    setCanStop(false);
    setIsStreaming(true);
    setCurrentDelta("");
    setCurrentProposals([]);

    // Armar el botón de detener tras breve período de guarda para evitar colisión con el clic de envío
    setTimeout(() => {
      if (isStreamingRef.current) {
        setCanStop(true);
      }
    }, 450);

    const notifyListeners = (event) => {
      streamEntry.listeners.forEach((listener) => {
        try {
          listener(event);
        } catch { /* no-op */ }
      });
    };

    await streamAssistantChat({
      payload: {
        project_id: currentProjectId,
        user_message: outboundUserMessage,
        provider_id: selectedModel.provider_id,
        model_id: selectedModel.model_id,
        history,
        active_script: activeScript ? {
          id: activeScript.id,
          versionId: activeScript.versionId,
          title: activeScript.title,
          content: activeScript.content,
        } : null,
      },
      signal: controller.signal,
      onConnect: (data) => {
        if (data.provider) capturedProvider = data.provider;
        if (data.model) capturedModel = data.model;
        streamEntry.capturedProvider = capturedProvider;
        streamEntry.capturedModel = capturedModel;
        notifyListeners({ type: "connect", provider: capturedProvider, model: capturedModel });
      },
      onDelta: (chunk) => {
        accumulatedText += chunk;
        streamEntry.accumulatedText = accumulatedText;
        setCurrentDelta(accumulatedText);
        notifyListeners({ type: "delta", text: accumulatedText });
      },
      onProposal: (proposal) => {
        const enriched = {
          ...proposal,
          id: (proposal.id && proposal.id !== "prop_1") ? proposal.id : `prop_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`,
          scriptId: proposal.scriptId || activeScript?.id || null,
          scriptVersionId: proposal.scriptVersionId || activeScript?.versionId || null,
          scriptTitle: proposal.scriptTitle || activeScript?.title || null,
        };
        accumulatedProposals.push(enriched);
        streamEntry.accumulatedProposals = [...accumulatedProposals];
        setCurrentProposals([...accumulatedProposals]);
        notifyListeners({ type: "proposal", proposals: [...accumulatedProposals] });
      },
      onUsage: (usage) => {
        capturedUsage = usage;
        streamEntry.capturedUsage = usage;
        notifyListeners({ type: "usage", usage });
      },
      onDone: () => {
        isStreamingRef.current = false;
        notifyListeners({ type: "done" });
        activeProjectStreams.delete(currentProjectId);

        setIsStreaming(false);
        setCanStop(false);
        setCurrentDelta("");
        setCurrentProposals([]);
        abortControllerRef.current = null;

        // Requisito 4: Si el stream termina sin texto ni propuesta válida, mostrar aviso legible y conservar borrador
        if (!accumulatedText.trim() && accumulatedProposals.length === 0) {
          const emptyNotice = "El stream del Director finalizó sin texto ni propuesta válida. Se conservó el borrador para reintentar el envío.";
          setStreamingError(emptyNotice);
          setText(savedText);
          if (savedDraftSceneContext) {
            setDraftSceneContext(savedDraftSceneContext);
          }
          if (savedIsScriptMode) {
            setIsScriptMode(true);
          }
          dispatch({
            type: "THREAD_MESSAGE",
            payload: {
              projectId: currentProjectId,
              message: {
                role: "assistant",
                content: `⚠️ ${emptyNotice}`,
                system: true,
                isError: true,
                cost_status: "unknown",
                cost_known: false,
                cost_usd: null,
              },
            },
          });
          return;
        }

        const cleanContent = (accumulatedText || "").replace(/<veyra_proposal>[\s\S]*?<\/veyra_proposal>/g, "").trim();
        const finalCostKnown = Boolean(capturedUsage?.cost_known && capturedUsage?.cost_estimate_usd !== null && capturedUsage?.cost_estimate_usd !== undefined);
        const isBilledAuthoritative = Boolean(capturedUsage?.is_billed || (capturedUsage?.cost_authoritative && capturedUsage?.is_billed));
        const assistantMessage = {
          role: "assistant",
          content: cleanContent || accumulatedText || "Respuesta completada.",
          proposals: accumulatedProposals,
          provider: capturedProvider,
          model: capturedModel,
          usage: capturedUsage,
          cost_status: isBilledAuthoritative ? "billed" : (finalCostKnown ? "calculated" : "unknown"),
          cost_known: finalCostKnown,
          cost_usd: finalCostKnown ? capturedUsage.cost_estimate_usd : null,
          cost_authoritative: isBilledAuthoritative,
          is_billed: isBilledAuthoritative,
          total_tokens: capturedUsage?.total_tokens ?? null,
          prompt_tokens: capturedUsage?.prompt_tokens ?? null,
          completion_tokens: capturedUsage?.completion_tokens ?? null,
          tokens_reported: Boolean(capturedUsage?.tokens_reported),
        };

        dispatch({
          type: "THREAD_MESSAGE",
          payload: { projectId: currentProjectId, message: assistantMessage },
        });
      },
      onCancelled: (cancelData) => {
        isStreamingRef.current = false;
        notifyListeners({ type: "cancelled", cancelData });
        activeProjectStreams.delete(currentProjectId);

        setIsStreaming(false);
        setCanStop(false);
        setCurrentDelta("");
        setCurrentProposals([]);
        abortControllerRef.current = null;

        const isInterrupted = Boolean(cancelData?.status === "interrupted" || cancelData?.interrupted);
        const cancelAction = cancelData?.action || (isInterrupted ? "connection_interrupted" : "unspecified_abort");

        if (accumulatedText.trim()) {
          // Requisito: Si recibió texto parcial, no debe tratarse como finalización correcta:
          // debe indicarse como respuesta interrumpida con coste desconocido.
          const statusTag = isInterrupted
            ? "*(Respuesta interrumpida)*"
            : "*(Generación detenida por el usuario)*";
          dispatch({
            type: "THREAD_MESSAGE",
            payload: {
              projectId: currentProjectId,
              message: {
                role: "assistant",
                content: accumulatedText + `\n\n${statusTag}`,
                proposals: accumulatedProposals,
                provider: capturedProvider,
                model: capturedModel,
                status: isInterrupted ? "interrupted" : "cancelled",
                cost_status: "unknown",
                cost_known: false,
                cost_usd: null, // Nunca 0.0
                total_tokens: capturedUsage?.total_tokens || null,
                tokens_reported: false,
              },
            },
          });
        } else {
          // Requisito: Si el stream termina sin texto ni propuesta válida, mostrar aviso legible y conservar borrador para reintento manual
          const cancelNotice = isInterrupted
            ? "La respuesta fue interrumpida por el servidor o conexión. Se conservó el borrador para que puedas reintentar el envío."
            : "La respuesta fue interrumpida o no devolvió contenido. Se conservó el borrador para que puedas reintentar el envío.";
          setStreamingError(cancelNotice);
          setText(savedText);
          if (savedDraftSceneContext) {
            setDraftSceneContext(savedDraftSceneContext);
          }
          if (savedIsScriptMode) {
            setIsScriptMode(true);
          }
          console.info("[CreativeDirector] Cancelación procesada:", {
            action: cancelAction,
            isInterrupted,
            hasDraft: Boolean(savedText),
            isScriptMode: Boolean(savedIsScriptMode),
          });
          dispatch({
            type: "THREAD_MESSAGE",
            payload: {
              projectId: currentProjectId,
              message: {
                role: "assistant",
                content: `⚠️ ${cancelNotice}`,
                system: true,
                isError: true,
                cost_status: "unknown",
                cost_known: false,
                cost_usd: null,
              },
            },
          });
        }
      },
      onError: (errDetail) => {
        isStreamingRef.current = false;
        setCanStop(false);
        const safeError = typeof errDetail === "string"
          ? errDetail
          : (errDetail?.message || errDetail?.detail || errDetail?.msg || "Error en el servicio de inferencia.");

        notifyListeners({ type: "error", error: safeError, savedText, savedDraftSceneContext });
        activeProjectStreams.delete(currentProjectId);

        setIsStreaming(false);
        setCurrentDelta("");
        setCurrentProposals([]);
        setStreamingError(safeError);
        abortControllerRef.current = null;

        // Conservar el borrador y contexto de la toma para permitir corregir o reintentar manualmente
        setText(savedText);
        if (savedDraftSceneContext) {
          setDraftSceneContext(savedDraftSceneContext);
        }
        if (savedIsScriptMode) {
          setIsScriptMode(true);
        }

        // Requisito: No añadir el rechazo de validación como si fuera una respuesta creativa del Director
        const isValidationError = /15000|422|validation|demasiado largo|límite|characters/i.test(safeError);
        if (!isValidationError) {
          dispatch({
            type: "THREAD_MESSAGE",
            payload: {
              projectId: currentProjectId,
              message: {
                role: "assistant",
                content: `⚠️ ${safeError}`,
                system: true,
                isError: true,
                cost_status: "unknown",
                cost_known: false,
                cost_usd: null,
              },
            },
          });
        }
      },
    });
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      e.stopPropagation();
      if (e.repeat || e.nativeEvent?.isComposing || isStreamingRef.current || isStreaming) {
        return;
      }
      send();
    }
  };

  return (
    <div
      ref={panelRef}
      className="relative flex h-full min-h-0 flex-1 flex-col bg-[#141414] select-text"
      data-testid="assistant-panel-body"
    >
      {/* 1. Header con branding y controles de ventana */}
      <div className="flex h-14 shrink-0 items-center justify-between border-b border-[#303030] bg-[#161616] px-4">
        <div className="flex min-w-0 items-center gap-2.5">
          <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border border-[#f5b544]/30 bg-[#f5b544]/10 text-[#f5b544]">
            <Sparkles size={16} />
          </div>
          <div className="min-w-0">
            <h2 className="truncate font-display text-sm font-semibold text-[#f4f1ec]">
              {t.assistant.title}
            </h2>
            <p className="truncate text-[11px] text-[#a3a3a3]">
              {t.assistant.subtitle}
            </p>
          </div>
        </div>

        {/* Controles de estado: expandir (50%), pantalla completa y contraer */}
        <div className="flex items-center gap-1">
          <button
            type="button"
            data-testid="assistant-toggle-expanded"
            onClick={() => onModeChange && onModeChange(mode === "expanded" ? "normal" : "expanded")}
            className={`rounded-md p-1.5 transition-colors ${
              mode === "expanded"
                ? "bg-[#f5b544]/20 text-[#f5b544]"
                : "text-[#a3a3a3] hover:bg-[#242424] hover:text-[#f4f1ec]"
            }`}
            title={mode === "expanded" ? t.assistant.modes.restore : t.assistant.modes.expanded}
            aria-label={mode === "expanded" ? t.assistant.modes.restore : t.assistant.modes.expanded}
          >
            {mode === "expanded" ? <Minimize2 size={15} /> : <Maximize2 size={15} />}
          </button>

          <button
            type="button"
            data-testid="assistant-toggle-fullscreen"
            onClick={() => onModeChange && onModeChange(mode === "fullscreen" ? "normal" : "fullscreen")}
            className={`rounded-md p-1.5 transition-colors ${
              mode === "fullscreen"
                ? "bg-[#f5b544]/20 text-[#f5b544]"
                : "text-[#a3a3a3] hover:bg-[#242424] hover:text-[#f4f1ec]"
            }`}
            title={mode === "fullscreen" ? t.assistant.modes.exitFullscreen : t.assistant.modes.fullscreen}
            aria-label={mode === "fullscreen" ? t.assistant.modes.exitFullscreen : t.assistant.modes.fullscreen}
          >
            {mode === "fullscreen" ? <Shrink size={15} /> : <Expand size={15} />}
          </button>

          <button
            type="button"
            data-testid="assistant-collapse-btn"
            onClick={onClose}
            className="rounded-md p-1.5 text-[#a3a3a3] hover:bg-[#242424] hover:text-[#f4f1ec]"
            title={t.assistant.modes.collapse}
            aria-label={t.assistant.modes.collapse}
          >
            <PanelRightClose size={15} />
          </button>
        </div>
      </div>

      {/* 2. Selector dinámico de modelos LLM desde el Backend */}
      <div
        className="flex shrink-0 flex-col gap-2 border-b border-[#262626] bg-[#121212] px-4 py-3"
        data-testid="assistant-provider-row"
      >
        <div className="flex items-center justify-between gap-2">
          <span className="v-eyebrow flex items-center gap-1.5 text-[#888]">
            <Cpu size={12} className="text-[#a3a3a3]" />
            Modelo cinematográfico
          </span>
          <button
            type="button"
            data-testid="assistant-continuity-inspect-btn"
            onClick={triggerContinuityInspect}
            disabled={continuityState.status === "loading"}
            className="flex items-center gap-1 rounded border border-[#303030] bg-[#1a1a1a] px-2 py-0.5 text-[10px] font-medium text-[#f5b544] hover:border-[#f5b544]/50 hover:bg-[#221c10] transition-colors disabled:opacity-50"
            title={typeof t.assistant.quickActions?.continuity?.label === 'function' ? t.assistant.quickActions.continuity.label() : t.assistant.quickActions?.continuity?.label || "Revisar continuidad"}
          >
            <Sliders size={11} />
            <span>{typeof t.assistant.quickActions?.continuity?.label === 'function' ? t.assistant.quickActions.continuity.label() : t.assistant.quickActions?.continuity?.label || "Revisar continuidad"}</span>
          </button>
        </div>

        {loadingModels ? (
          <div className="flex items-center gap-2 rounded-lg border border-[#262626] bg-[#0c0c0c] p-2 text-xs text-[#888]">
            <Loader2 size={13} className="animate-spin text-[#f5b544]" />
            <span>Cargando modelos configurados...</span>
          </div>
        ) : models.length === 0 ? (
          /* Estado sin conexiones configuradas */
          <div
            data-testid="assistant-provider-status"
            className="flex items-start gap-2.5 rounded-lg border border-[#f5b544]/25 bg-[#f5b544]/5 p-2.5 text-xs leading-relaxed"
          >
            <AlertCircle size={14} className="mt-0.5 shrink-0 text-[#f5b544]" />
            <div className="min-w-0">
              <div className="font-semibold text-[#f5b544]">
                {t.assistant.statusPending}
              </div>
              <div className="text-[11px] text-[#a3a3a3]">
                {modelsError || "No hay modelos LLM activos con API key configurada. Ve a Panel Administrativo > Conexiones para activar la inferencia."}
              </div>
            </div>
          </div>
        ) : (
          /* Selector dinámico de modelos disponibles */
          <div className="flex flex-col gap-1.5" data-testid="assistant-model-selector">
            <select
              data-testid="assistant-model-select"
              value={selectedModelKey}
              onChange={(e) => handleModelSelect(e.target.value)}
              disabled={isStreaming}
              className="w-full rounded-md border border-[#303030] bg-[#181818] px-2.5 py-1.5 text-xs font-medium text-[#f4f1ec] outline-none transition focus:border-[#f5b544] disabled:opacity-50"
            >
              {models.map((m) => {
                const labelText = `${m.model_label} (${m.provider_label}) · ${m.is_rate_verified ? "Tarifa oficial" : "Tarifa orientativa"}`;
                return (
                  <option
                    key={`${m.provider_id}:${m.model_id}`}
                    value={`${m.provider_id}:${m.model_id}`}
                    data-testid={`assistant-model-option-${m.model_id}`}
                  >
                    {labelText}
                  </option>
                );
              })}
            </select>

            {/* Badges descriptivos del modelo seleccionado */}
            {currentSelectedModel && (
              <div className="flex flex-wrap items-center gap-1.5 text-[10px] text-[#888] pt-0.5">
                <span className="rounded bg-[#202020] px-1.5 py-0.5 border border-[#333] text-[#aaa]">
                  {currentSelectedModel.provider_label}
                </span>
                {(currentSelectedModel.capabilities || []).map((c) => (
                  <span key={c} className="rounded bg-[#1a1a1a] px-1 py-0.5 text-[#777]">
                    {c}
                  </span>
                ))}
                {currentSelectedModel.is_rate_verified ? (
                  <span className="flex items-center gap-1 rounded bg-[#064e3b]/30 px-1.5 py-0.5 text-[#34d399] border border-[#059669]/30">
                    <CheckCircle2 size={10} />
                    Tarifa oficial
                  </span>
                ) : (
                  <span className="flex items-center gap-1 rounded bg-[#78350f]/20 px-1.5 py-0.5 text-[#f5b544] border border-[#b45309]/30">
                    <HelpCircle size={10} />
                    Tarifa orientativa
                  </span>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {/* 2.4 Banner de Guion Activo del Proyecto */}
      <div
        data-testid="assistant-active-script-banner"
        className="shrink-0 border-b border-[#262626] bg-[#151515] px-4 py-2 flex items-center justify-between gap-2 text-xs"
      >
        <div className="flex items-center gap-2 min-w-0">
          <div className="h-6 w-6 rounded bg-[#222] border border-[#333] flex items-center justify-center text-[#f5b544] shrink-0">
            <FileText size={12} />
          </div>
          {activeScript ? (
            <div className="flex items-center gap-1.5 min-w-0 truncate">
              <span className="font-medium text-[#f4f1ec] truncate" data-testid="assistant-active-script-title">
                {activeScript.title}
              </span>
              <span className="rounded bg-[#202020] px-1 py-0.2 text-[9px] font-mono text-[#aaa] border border-[#333] shrink-0">
                {activeScript.versionId || "v1"}
              </span>
              <span className="rounded bg-[#064e3b]/30 text-[#34d399] px-1.5 py-0.2 text-[9px] font-medium border border-[#059669]/30 shrink-0 flex items-center gap-0.5">
                <Check size={9} />
                Activo
              </span>
            </div>
          ) : (
            <span className="text-[11px] text-[#777] truncate">
              Sin guion activo en el proyecto
            </span>
          )}
        </div>

        <div className="flex items-center gap-2 shrink-0">
          {activeScript ? (
            <>
              <button
                type="button"
                data-testid="assistant-read-script-btn"
                onClick={() => setIsScriptModalOpen(true)}
                className="text-[#f5b544] hover:underline text-[11px] font-medium cursor-pointer"
              >
                Leer guion
              </button>
              <span className="text-[#444]">·</span>
              <button
                type="button"
                data-testid="assistant-change-script-btn"
                onClick={() => setIsScriptModalOpen(true)}
                className="text-[#aaa] hover:text-[#f4f1ec] text-[11px] cursor-pointer"
              >
                Cambiar
              </button>
            </>
          ) : (
            <button
              type="button"
              data-testid="assistant-select-script-btn"
              onClick={() => setIsScriptModalOpen(true)}
              className="text-[#f5b544] hover:underline text-[11px] font-medium cursor-pointer"
            >
              Elegir guion
            </button>
          )}
        </div>
      </div>

      {/* 2.5 Panel consultivo de alertas de continuidad */}
      {continuityState.status !== "idle" && (
        <div
          data-testid="assistant-continuity-panel"
          className={`shrink-0 border-b border-[#262626] bg-[#111111] px-4 py-3 text-xs flex flex-col ${
            mode === "fullscreen"
              ? "max-h-[min(68vh,660px)]"
              : mode === "expanded"
              ? "max-h-[min(58vh,540px)]"
              : "max-h-[min(48vh,420px)]"
          }`}
        >
          {/* Header de continuidad y advertencia epistémica fijados arriba (nunca se desplazan fuera de vista) */}
          <div className="shrink-0 flex flex-col gap-1.5 pb-2.5 border-b border-[#222]">
            <div className="flex items-center justify-between gap-2">
              <div className="flex items-center gap-1.5 font-semibold text-[#f4f1ec]">
                <Sliders size={13} className="text-[#f5b544]" />
                <span>{t.assistant.continuityInspectionTitle}</span>
                {continuityState.status === "success" && (
                  <span
                    data-testid="assistant-continuity-count-badge"
                    className={`rounded px-1.5 py-0.2 text-[10px] font-medium border ${
                      attentionObservations.length > 0
                        ? "border-[#f5b544]/40 bg-[#f5b544]/10 text-[#f5b544]"
                        : "border-[#059669]/40 bg-[#064e3b]/20 text-[#34d399]"
                    }`}
                  >
                    {attentionObservations.length}
                  </span>
                )}
              </div>
              <div className="flex items-center gap-1">
                <button
                  type="button"
                  data-testid="assistant-continuity-refresh-btn"
                  onClick={triggerContinuityInspect}
                  disabled={continuityState.status === "loading"}
                  className="rounded p-1 text-[#888] hover:text-[#f4f1ec] transition-colors"
                  title={t.assistant.continuityRetry}
                >
                  <RotateCcw size={12} className={continuityState.status === "loading" ? "animate-spin" : ""} />
                </button>
                <button
                  type="button"
                  data-testid="assistant-continuity-close-btn"
                  onClick={() => setContinuityState((s) => ({ ...s, status: "idle" }))}
                  className="rounded p-1 text-[#888] hover:text-[#f4f1ec] transition-colors"
                  title={t.assistant.continuityDismiss}
                >
                  <X size={12} />
                </button>
              </div>
            </div>

            {/* Advertencia epistémica estricta: análisis textual, no visión artificial */}
            <div className="text-[10px] text-[#777] flex items-center gap-1.5">
              <Info size={11} className="shrink-0 text-[#888]" />
              <span>{t.continuity.disclaimer}</span>
            </div>
          </div>

          {/* Cuerpo desplazable: accesible sin ocultar la cabecera y sin invadir el chat */}
          <div
            data-testid="assistant-continuity-scroll-body"
            className="flex-1 min-h-0 overflow-y-auto pt-2.5 pr-1 flex flex-col gap-2.5"
          >
            {/* Aviso claro si una toma ya no existe en el proyecto activo */}
            {missingSceneWarning && (
              <div
                data-testid="assistant-continuity-missing-scene-notice"
                className="flex items-center justify-between gap-2 rounded-lg border border-amber-500/40 bg-amber-500/10 p-2.5 text-xs text-amber-300"
              >
                <div className="flex items-center gap-2 min-w-0">
                  <AlertTriangle size={14} className="shrink-0 text-amber-400" />
                  <span className="truncate">{missingSceneWarning.message}</span>
                </div>
                <button
                  type="button"
                  data-testid="assistant-continuity-missing-scene-dismiss"
                  onClick={() => setMissingSceneWarning(null)}
                  className="text-amber-400/70 hover:text-amber-200 p-0.5 rounded transition-colors"
                  title="Cerrar aviso"
                >
                  <X size={12} />
                </button>
              </div>
            )}

            {/* Estado de carga */}
            {continuityState.status === "loading" && (
              <div
                data-testid="assistant-continuity-loading"
                className="flex items-center gap-2 rounded-lg border border-[#2a2a2a] bg-[#161616] p-3 text-xs text-[#aaa]"
              >
                <Loader2 size={14} className="animate-spin text-[#f5b544]" />
                <span>{t.assistant.continuityInspecting}</span>
              </div>
            )}

            {/* Estado de error */}
            {continuityState.status === "error" && (
              <div
                data-testid="assistant-continuity-error"
                className="flex items-start gap-2.5 rounded-lg border border-[#ef4444]/30 bg-[#2b1416] p-3 text-xs text-[#ffb4b7]"
              >
                <AlertCircle size={15} className="mt-0.5 shrink-0 text-[#ef4444]" />
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-[#fca5a5]">
                    {continuityState.error || t.assistant.continuityError}
                  </div>
                  <button
                    type="button"
                    data-testid="assistant-continuity-retry-btn"
                    onClick={triggerContinuityInspect}
                    className="mt-2 inline-flex items-center gap-1 rounded border border-[#ef4444]/40 bg-[#3a181b] px-2 py-0.5 text-[11px] font-medium text-[#fca5a5] hover:bg-[#481c20]"
                  >
                    <RotateCcw size={11} />
                    {t.assistant.continuityRetry}
                  </button>
                </div>
              </div>
            )}

            {/* Estado limpio de atención (0 contradicciones / riesgos) */}
            {continuityState.status === "success" && attentionObservations.length === 0 && (
              <div
                data-testid="assistant-continuity-empty"
                className="flex items-start gap-2.5 rounded-lg border border-[#059669]/30 bg-[#064e3b]/15 p-3 text-xs text-[#34d399]"
              >
                <CheckCircle2 size={15} className="mt-0.5 shrink-0 text-[#34d399]" />
                <div>
                  <div className="font-semibold text-[#a7f3d0]">{t.assistant.continuityClean}</div>
                  <div className="text-[11px] text-[#a7f3d0]/80 mt-0.5">{t.assistant.continuityCleanDesc}</div>
                </div>
              </div>
            )}

            {/* Renderizado de observaciones individuales */}
            {(() => {
              const renderContinuityCard = (obs) => {
                const isConflict = obs.severity === "conflict";
                const isWarning = obs.severity === "warning";
                const isMissing = obs.risk_type === CONTINUITY_RISK_TYPES.MISSING_INFORMATION;
                const isSingleScene = obs.id?.includes("single_scene_scope");

                const severityBadge = isSingleScene
                  ? { label: t.continuity?.analysisScope || "Alcance del análisis", class: "border-zinc-500/40 bg-zinc-500/10 text-zinc-300" }
                  : isConflict
                  ? { label: t.continuity.categories.real_contradiction, class: "border-red-500/40 bg-red-500/10 text-red-400" }
                  : isWarning
                  ? { label: t.continuity.categories.possible_risk, class: "border-amber-500/40 bg-amber-500/10 text-amber-400" }
                  : isMissing
                  ? { label: t.continuity.categories.missing_information, class: "border-sky-500/40 bg-sky-500/10 text-sky-300" }
                  : { label: t.continuity.categories.registered_unused_asset, class: "border-zinc-500/40 bg-zinc-500/10 text-zinc-400" };

                const affectedSceneIds = Array.isArray(obs.affected_scene_ids)
                  ? obs.affected_scene_ids
                  : Array.isArray(obs.affectedSceneIds)
                  ? obs.affectedSceneIds
                  : [];

                return (
                  <div
                    key={obs.id}
                    data-testid={`assistant-continuity-alert-${obs.id}`}
                    className={`rounded-lg border p-3 text-xs flex flex-col gap-2 ${
                      isConflict
                        ? "border-red-500/30 bg-[#1c1213]"
                        : isWarning
                        ? "border-amber-500/30 bg-[#1b1710]"
                        : "border-[#2b2b2b] bg-[#161616]"
                    }`}
                  >
                    {/* Header de la alerta: severidad e ícono */}
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex flex-wrap items-center gap-1.5">
                        <span className={`rounded border px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wider ${severityBadge.class}`}>
                          {severityBadge.label}
                        </span>
                        <span className="font-semibold text-[#f4f1ec]">{obs.title}</span>
                      </div>
                    </div>

                    {/* Explicación / Descripción */}
                    <p className="text-[11px] leading-relaxed text-[#ccc]">{obs.description}</p>

                    {/* Evidencia textual y datos faltantes */}
                    {obs.evidence && Object.keys(obs.evidence).length > 0 && (
                      <div
                        data-testid={`assistant-continuity-evidence-${obs.id}`}
                        className="rounded bg-[#0c0c0c] border border-[#222] p-2 text-[10px] font-mono text-[#aaa] space-y-1"
                      >
                        {obs.evidence.canon_source && (
                          <div>
                            <span className="text-[#777]">Fuente:</span> {obs.evidence.canon_source}
                          </div>
                        )}
                        {obs.evidence.canon_text && (
                          <div>
                            <span className="text-[#777]">Canon:</span> <span className="text-[#34d399] font-medium">"{obs.evidence.canon_text}"</span>
                          </div>
                        )}
                        {obs.evidence.scene_text && (
                          <div>
                            <span className="text-[#777]">Texto toma:</span>{" "}
                            {isSingleScene ? (
                              <div className="mt-1 font-sans">
                                <span className="text-[#ccc]">
                                  {t.continuity?.singleSceneReviewedBrief || "Texto revisado sin inconsistencias internas."}
                                </span>{" "}
                                <button
                                  type="button"
                                  data-testid={`assistant-continuity-toggle-prompt-${obs.id}`}
                                  onClick={() => setExpandedPrompts((prev) => ({ ...prev, [obs.id]: !prev[obs.id] }))}
                                  className="text-[#f5b544] hover:underline cursor-pointer text-[10px] inline-block ml-1 font-medium"
                                >
                                  {expandedPrompts[obs.id]
                                    ? (t.continuity?.hideFullPrompt || "Ocultar texto")
                                    : (t.continuity?.viewFullPrompt || "Ver texto completo")}
                                </button>
                                {expandedPrompts[obs.id] && (
                                  <div className="mt-1.5 max-h-32 overflow-y-auto rounded bg-[#161616] p-2 text-[10px] font-mono text-[#bbb] whitespace-pre-wrap border border-[#2a2a2a] leading-relaxed">
                                    "{obs.evidence.scene_text}"
                                  </div>
                                )}
                              </div>
                            ) : (
                              <span className={isConflict ? "text-[#f87171] font-medium" : "text-[#ccc]"}>
                                "{obs.evidence.scene_text}"
                              </span>
                            )}
                          </div>
                        )}
                        {obs.evidence.prev_scene_text && (
                          <div>
                            <span className="text-[#777]">Toma anterior (#{obs.evidence.prev_scene_order}):</span> "{obs.evidence.prev_scene_text}"
                          </div>
                        )}
                        {obs.evidence.target_scene_text && !obs.evidence.scene_text && (
                          <div>
                            <span className="text-[#777]">Toma actual (#{obs.evidence.target_scene_order}):</span>{" "}
                            <span className={isConflict ? "text-[#f87171] font-medium" : "text-[#ccc]"}>
                              "{obs.evidence.target_scene_text}"
                            </span>
                          </div>
                        )}
                      </div>
                    )}

                    {/* Avisos específicos de cámara */}
                    {obs.id?.includes("missing_camera") && (
                      <p className="text-[10px] text-sky-400/90 italic">
                        {t.assistant.missingCameraNotice}
                      </p>
                    )}
                    {obs.id?.includes("unstructured_camera") && (
                      <p className="text-[10px] text-sky-400/90 italic">
                        {t.assistant.unstructuredCameraNotice || "Sin parámetros estructurados de cámara; el prompt contiene indicaciones."}
                      </p>
                    )}

                    {/* Recomendación consultiva */}
                    <div className="rounded border border-[#f5b544]/20 bg-[#1c1810] p-2 text-[11px] text-[#e0cfb3] flex items-start gap-1.5">
                      <Sparkles size={12} className="text-[#f5b544] shrink-0 mt-0.5" />
                      <div className="leading-relaxed">
                        <strong className="text-[#f5b544] font-medium">{t.continuity.recommendation}:</strong> {obs.recommendation}
                      </div>
                    </div>

                    {/* Propuesta de corrección consultiva (sin auto-apply) */}
                    {obs.proposed_fix && (
                      <div
                        data-testid={`assistant-continuity-fix-${obs.id}`}
                        className="rounded bg-[#121212] border border-[#2d2d2d] p-2 text-[11px] flex flex-col gap-1 text-[#aaa]"
                      >
                        <span className="text-[10px] text-[#f5b544] font-medium">
                          {t.continuity.proposedChange} ({t.assistant.consultiveNotice}):
                        </span>
                        <div className="font-mono text-[10px] text-[#888] line-through whitespace-pre-wrap">
                          {obs.proposed_fix.before_text}
                        </div>
                        <div className="font-mono text-[10px] text-[#a7f3d0] whitespace-pre-wrap">
                          {obs.proposed_fix.after_text}
                        </div>
                        {obs.proposed_fix.explanation && (
                          <div className="text-[10px] text-[#888] italic">
                            {obs.proposed_fix.explanation}
                          </div>
                        )}
                      </div>
                    )}

                    {/* Navegación a las escenas afectadas (sin perder conversación) */}
                    {affectedSceneIds.length > 0 && (
                      <div className="flex flex-wrap items-center gap-1.5 pt-1 border-t border-[#262626]">
                        <span className="text-[10px] text-[#888]">{t.continuity.affectedScenes}:</span>
                        {affectedSceneIds.map((scId) => {
                          const sc = (data.scenes || []).find((s) => s.id === scId);
                          const scOrder = sc ? (sc.order ?? 0) + 1 : "?";
                          const scTitle = sc ? sc.title || `Toma #${scOrder}` : scId;
                          return (
                            <button
                              key={scId}
                              type="button"
                              data-testid={`assistant-continuity-scene-link-${obs.id}-${scId}`}
                              onClick={() => handleNavigateToScene(scId, obs)}
                              className="inline-flex items-center gap-1 rounded bg-[#222] border border-[#333] px-1.5 py-0.5 text-[10px] font-medium text-[#ccc] hover:border-[#f5b544]/60 hover:text-[#f4f1ec] transition-colors cursor-pointer"
                              title={`${t.assistant.openInStoryboard || t.assistant.openScene}: ${scTitle}`}
                            >
                              <ArrowRight size={10} className="text-[#f5b544]" />
                              <span>Toma #{scOrder}: {scTitle}</span>
                            </button>
                          );
                        })}
                      </div>
                    )}

                    {/* Solicitar corrección en chat (únicamente si el raccord es corregible modificando el prompt) */}
                    {isObservationFixable(obs) && (
                      <div className="pt-1.5 flex flex-col gap-1 border-t border-[#262626]">
                        {pickingSceneForObsId === obs.id ? (
                          <div
                            data-testid={`assistant-continuity-pick-scene-panel-${obs.id}`}
                            className="rounded bg-[#1c1810] border border-[#f5b544]/40 p-2 flex flex-col gap-1.5"
                          >
                            <span className="text-[10px] font-medium text-[#f5b544]">
                              {t.assistant?.requestFixPickScene || "¿En qué toma deseas solicitar la corrección?"}
                            </span>
                            <div className="flex flex-wrap gap-1.5">
                              {affectedSceneIds.map((scId) => {
                                const sc = (data.scenes || []).find((s) => s.id === scId);
                                const scOrder = sc ? (sc.order ?? 0) + 1 : "?";
                                const scTitle = sc ? sc.title || `Toma #${scOrder}` : scId;
                                return (
                                  <button
                                    key={scId}
                                    type="button"
                                    data-testid={`assistant-continuity-pick-scene-btn-${obs.id}-${scId}`}
                                    onClick={() => handleRequestContinuityFix(obs, scId)}
                                    className="v-btn-primary v-btn-sm text-[10px] py-0.5 px-2"
                                  >
                                    Toma #{scOrder}: {scTitle}
                                  </button>
                                );
                              })}
                              <button
                                type="button"
                                onClick={() => setPickingSceneForObsId(null)}
                                className="v-btn-ghost v-btn-sm text-[10px] py-0.5 px-1.5 text-[#888]"
                              >
                                {t.common?.cancel || "Cancelar"}
                              </button>
                            </div>
                          </div>
                        ) : (
                          <div className="flex items-center justify-between gap-2">
                            <button
                              type="button"
                              data-testid={`assistant-continuity-request-fix-${obs.id}`}
                              onClick={() => handleRequestContinuityFix(obs)}
                              className="inline-flex items-center gap-1 rounded bg-[#201c13] border border-[#f5b544]/50 px-2 py-1 text-[11px] font-medium text-[#f5b544] hover:bg-[#2a2215] transition-colors cursor-pointer"
                              title={t.assistant?.requestFix || "Solicitar corrección"}
                            >
                              <Sparkles size={11} className="text-[#f5b544]" />
                              <span>{t.assistant?.requestFix || "Solicitar corrección"}</span>
                            </button>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              };

              if (continuityState.status !== "success" || continuityState.observations.length === 0) {
                return null;
              }

              return (
                <div
                  data-testid="assistant-continuity-list"
                  className="flex flex-col gap-2.5"
                >
                  {/* 1. Alertas que requieren atención (contradicciones y riesgos) */}
                  {attentionObservations.map(renderContinuityCard)}

                  {/* 2. Sección informativa de reparto y notas consultivas (abre modal independiente sin desplegable recortado) */}
                  {informativeObservations.length > 0 && (
                    <div className="flex flex-col gap-2 mt-1">
                      <button
                        type="button"
                        data-testid="assistant-continuity-informative-toggle"
                        onClick={() => setInformativeModalOpen(true)}
                        className="flex w-full items-center justify-between rounded-md border border-[#2a2a2a] bg-[#161616] px-3 py-2 text-xs font-medium text-[#a3a3a3] hover:text-[#f4f1ec] hover:border-[#383838] transition-colors cursor-pointer group"
                      >
                        <span className="flex items-center gap-1.5">
                          <Info size={12} className="text-[#888] group-hover:text-[#f5b544] transition-colors" />
                          <span>{t.continuity.informativeTitle}</span>
                          <span className="rounded-full bg-[#202020] px-1.5 py-0.2 font-mono text-[10px] text-[#888]">
                            {informativeObservations.length}
                          </span>
                        </span>
                        <ExternalLink size={13} className="text-[#666] group-hover:text-[#aaa] transition-colors" />
                      </button>
                    </div>
                  )}
                </div>
              );
            })()}
          </div>
        </div>
      )}

      {/* Modal independiente para información de reparto y notas consultivas */}
      <InformativeContinuityModal
        open={informativeModalOpen}
        onClose={() => setInformativeModalOpen(false)}
        observations={informativeObservations}
        data={data}
        onNavigateToScene={handleNavigateToScene}
        onRequestFix={handleRequestContinuityFix}
        container={panelRef?.current}
      />

      {/* Modal para selección, previsualización y gestión de guiones */}
      <ScriptSelectionModal
        open={isScriptModalOpen}
        onClose={() => setIsScriptModalOpen(false)}
        projectId={project?.id}
        data={data}
        container={panelRef?.current}
      />

      {/* 3. Área desplazable de mensajes */}
      <div className="min-h-0 flex-1 overflow-y-auto p-4 space-y-4">
        {thread.messages.length === 0 && !isStreaming ? (
          /* Estado vacío con bienvenida y acciones rápidas */
          <div data-testid="assistant-empty" className="flex flex-col gap-4 py-2">
            <div className="rounded-xl border border-[#2a2a2a] bg-[#181818]/70 p-4 text-center">
              <div className="mx-auto flex h-10 w-10 items-center justify-center rounded-full border border-[#f5b544]/30 bg-[#f5b544]/10 text-[#f5b544] mb-2.5">
                <Sparkles size={20} />
              </div>
              <h3 className="font-display text-sm font-semibold text-[#f4f1ec]">
                {t.assistant.welcomeTitle}
              </h3>
              <p className="mt-1 text-xs leading-relaxed text-[#a3a3a3]">
                {t.assistant.welcomeBody}
              </p>
            </div>

            {/* Acciones Rápidas */}
            <div className="space-y-2">
              <span className="v-eyebrow px-1 text-[#888]">
                {t.assistant.quickActionsTitle}
              </span>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {QUICK_ACTIONS.map(({ key, icon: Icon, label, prompt }) => (
                  <button
                    key={key}
                    type="button"
                    data-testid={`assistant-quick-action-${key}`}
                    onClick={() => handleQuickAction(key, prompt())}
                    className="flex items-center gap-2.5 rounded-lg border border-[#262626] bg-[#171717] p-2.5 text-left text-xs text-[#d8d4cd] transition-all hover:border-[#f5b544]/50 hover:bg-[#201c13] hover:text-[#f4f1ec] active:scale-[0.99]"
                  >
                    <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md border border-[#303030] bg-[#111] text-[#f5b544]">
                      <Icon size={13} />
                    </div>
                    <span className="font-medium">{label()}</span>
                  </button>
                ))}
              </div>
            </div>
          </div>
        ) : (
          /* Lista de mensajes de conversación */
          <div className="flex flex-col gap-3.5">
            {/* Banner de planificación activa y alerta de vigencia */}
            {project?.activePlan && (
              planValidity.isOutdated ? (
                <div
                  data-testid="assistant-plan-outdated-alert"
                  className="p-3 rounded-xl bg-amber-500/10 border border-amber-500/30 text-amber-200 text-xs flex flex-col gap-2 shadow-sm"
                >
                  <div className="flex items-center justify-between">
                    <span className="font-semibold flex items-center gap-1.5 text-amber-300">
                      <AlertTriangle className="w-4 h-4 text-amber-400 shrink-0" />
                      La planificación necesita revisión
                    </span>
                    <span className="text-[10px] text-amber-300/80 font-mono px-1.5 py-0.5 rounded bg-amber-500/20 border border-amber-500/30">
                      {getCanonicalPlanDisplay(project.activePlan).canonicalVersion}
                    </span>
                  </div>
                  <div className="text-[11px] text-amber-200/90 space-y-0.5">
                    {planValidity.reasons.map((r, i) => (
                      <p key={i}>• {r}</p>
                    ))}
                  </div>
                  <div className="flex items-center gap-2 pt-1 border-t border-amber-500/20">
                    <button
                      type="button"
                      onClick={handleRequestPlanUpdate}
                      className="px-2.5 py-1 rounded bg-amber-500/20 hover:bg-amber-500/30 text-amber-200 text-[11px] font-medium border border-amber-500/30 transition-colors"
                    >
                      Solicitar actualización
                    </button>
                    <button
                      type="button"
                      onClick={() => setActivePlanModalOpen(true)}
                      className="px-2.5 py-1 rounded bg-slate-800 hover:bg-slate-700 text-slate-300 text-[11px] transition-colors"
                    >
                      Ver plan actual
                    </button>
                    <button
                      type="button"
                      onClick={handleClearActivePlan}
                      className="ml-auto text-[10px] text-slate-400 hover:text-slate-200"
                    >
                      Quitar de activa
                    </button>
                  </div>
                </div>
              ) : (
                <div
                  data-testid="assistant-active-plan-banner"
                  className="px-3 py-2 rounded-xl bg-emerald-950/40 border border-emerald-500/30 text-emerald-200 text-xs flex items-center justify-between shadow-sm"
                >
                  <div className="flex items-center gap-2 min-w-0">
                    <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400 shrink-0" />
                    <span className="truncate">
                      Planificación activa: <strong>{getCanonicalPlanDisplay(project.activePlan).displayTitle}</strong> ({getCanonicalPlanDisplay(project.activePlan).canonicalVersion})
                    </span>
                  </div>
                  <div className="flex items-center gap-2.5 shrink-0">
                    <button
                      type="button"
                      onClick={() => setActivePlanModalOpen(true)}
                      className="text-[11px] font-medium text-emerald-300 hover:text-emerald-100 underline transition-colors"
                    >
                      Revisar
                    </button>
                    <button
                      type="button"
                      onClick={handleClearActivePlan}
                      className="text-[10px] text-slate-400 hover:text-slate-200 transition-colors"
                      title="Quitar de activa"
                    >
                      Quitar
                    </button>
                  </div>
                </div>
              )
            )}

            {thread.messages.map((m) => {
              const isUser = m.role === "user";
              return (
                <div
                  key={m.id}
                  data-testid={`assistant-message-${m.id}`}
                  className={`flex gap-2.5 ${isUser ? "flex-row-reverse" : "flex-row"}`}
                >
                  {/* Avatar */}
                  <div
                    className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-full border text-xs ${
                      isUser
                        ? "border-[#594214] bg-[#f5b544] text-[#1a1206] font-semibold"
                        : "border-[#333] bg-[#202020] text-[#f5b544]"
                    }`}
                  >
                    {isUser ? <User size={13} /> : <Bot size={13} />}
                  </div>

                  {/* Columna de mensaje: Tarjeta + Barra discreta inferior */}
                  <div className={`flex flex-col min-w-0 ${isUser ? "max-w-[85%]" : "flex-1 max-w-[95%] sm:max-w-[92%]"} gap-1.5`}>
                    {/* Tarjeta de mensaje (contenido de la IA y propuestas) */}
                    <div
                      className={`rounded-xl p-3 text-xs leading-relaxed ${
                        isUser
                          ? "border border-[#543e14] bg-[#221a0d] text-[#f4f1ec]"
                          : m.isError
                          ? "border border-[#e5484d]/30 bg-[#2b1416] text-[#ffb4b7]"
                          : "border border-[#2a2a2a] bg-[#191919] text-[#d8d4cd]"
                      }`}
                    >
                      {/* Header de mensaje */}
                      <div className="mb-2 flex items-center justify-between gap-2 text-[10px] text-[#888]">
                        <span
                          className="font-medium text-[#aaa] truncate min-w-0"
                          title={
                            isUser
                              ? t.common?.you || "Tú"
                              : m.provider && m.model
                              ? `${t.assistant.title} · ${m.provider} (${m.model})`
                              : t.assistant.title
                          }
                        >
                          {isUser
                            ? t.common?.you || "Tú"
                            : m.provider && m.model
                            ? `${t.assistant.title} · ${m.provider} (${m.model})`
                            : t.assistant.title}
                        </span>
                        {m.isDemo && <DemoBadge />}
                      </div>

                      {/* Contenido: Markdown formateado para el asistente, texto preservado para el usuario */}
                      {isUser || m.isError ? (
                        <div className="whitespace-pre-wrap">{m.content}</div>
                      ) : (
                        <MarkdownContent
                          content={
                            m.content
                              ? m.content
                                  .replace(/<veyra_proposal>[\s\S]*?<\/veyra_proposal>/g, "")
                                  .replace(/<veyra_plan>[\s\S]*?<\/veyra_plan>/g, "")
                                  .trim()
                              : ""
                          }
                        />
                      )}

                      {/* Propuestas cinematográficas estructuradas */}
                      {((Array.isArray(m.proposals) && m.proposals.length > 0)
                        ? m.proposals
                        : extractProposalsFromContent(m.content)
                      ).map((p, pIdx) => (
                        <Proposal key={p.id || `prop_${m.id}_${pIdx}`} proposal={p} projectId={project.id} data={data} messageId={m.id} />
                      ))}

                      {/* Planes de producción estructurados */}
                      {!isUser && !m.isError && extractPlansFromContent(m.content, {
                        projectId: project?.id,
                        messageId: m.id,
                        scenes: data?.scenes || [],
                        proposals: (thread?.messages || []).flatMap((msg) => msg.proposals || []),
                        activeScript,
                      }).map((plan, planIdx) => (
                        <ProductionPlanCard
                          key={plan.id || `plan_${m.id}_${planIdx}`}
                          plan={plan}
                          project={project}
                          isActive={
                            Boolean(
                              project?.activePlan &&
                              (project.activePlan.id === plan.id ||
                                (project.activePlan.version === plan.version &&
                                  project.activePlan.title === plan.title))
                            )
                          }
                          onActivatePlan={handleActivatePlan}
                          onRequestChanges={handleRequestChangesOnPlan}
                        />
                      ))}
                    </div>

                    {/* Barra discreta justo debajo de la tarjeta: Consumo y acciones de copia */}
                    {!isUser && !m.system && (
                      <div
                        data-testid={`assistant-message-meta-${m.id}`}
                        className="flex flex-wrap items-center justify-between gap-2 px-1 text-[10px] text-[#888]"
                      >
                        {/* Insignias de consumo: tokens y coste */}
                        <div className="flex flex-wrap items-center gap-1.5">
                          {/* Tokens */}
                          {m.total_tokens !== null && m.total_tokens !== undefined ? (
                            <span
                              data-testid={`assistant-tokens-${m.id}`}
                              className="rounded bg-[#202020] px-1.5 py-0.5 border border-[#303030]"
                            >
                              {m.total_tokens} tokens {m.tokens_reported ? "(reportados)" : "(aprox.)"}
                            </span>
                          ) : null}

                          {/* Costes */}
                          {m.status === "cancelled" ? (
                            <span
                              data-testid={`assistant-cost-${m.id}`}
                              className="rounded bg-[#252525] px-1.5 py-0.5 border border-[#3a3a3a] text-[#999]"
                            >
                              Coste desconocido (cancelado)
                            </span>
                          ) : !m.cost_known || m.cost_usd === null || m.cost_usd === undefined ? (
                            <span
                              data-testid={`assistant-cost-${m.id}`}
                              className="rounded bg-[#252525] px-1.5 py-0.5 border border-[#3a3a3a] text-[#999]"
                            >
                              Coste desconocido
                            </span>
                          ) : (m.cost_status === "billed" || (m.cost_status === "authoritative" && m.is_billed)) ? (
                            <span
                              data-testid={`assistant-cost-${m.id}`}
                              className="rounded bg-[#064e3b]/30 px-1.5 py-0.5 text-[#34d399] border border-[#059669]/30"
                            >
                              US$ {Number(m.cost_usd).toFixed(6)} (facturado)
                            </span>
                          ) : (
                            <span
                              data-testid={`assistant-cost-${m.id}`}
                              className="rounded bg-[#78350f]/20 px-1.5 py-0.5 text-[#f5b544] border border-[#b45309]/30"
                            >
                              US$ {Number(m.cost_usd).toFixed(6)} (calculado)
                            </span>
                          )}
                        </div>

                        {/* Botones de copia: alineados a la derecha fuera de la tarjeta */}
                        {!m.isError && m.content && (
                          <div
                            className="flex items-center gap-1.5 shrink-0 ml-auto"
                            data-testid={`assistant-actions-${m.id}`}
                          >
                            <button
                              type="button"
                              data-testid={`assistant-copy-btn-${m.id}`}
                              onClick={() => handleCopy(m.content, m.id, "simple")}
                              className="inline-flex items-center gap-1 rounded bg-[#202020] hover:bg-[#2c2c2c] px-2 py-0.5 text-[10px] font-medium text-[#d4d4d4] hover:text-[#f4f1ec] transition-colors border border-[#333] shadow-sm cursor-pointer"
                              title="Copiar respuesta"
                              aria-label="Copiar respuesta"
                            >
                              {copiedState?.id === m.id && copiedState?.type === "simple" ? (
                                <>
                                  <Check size={11} className="text-emerald-400 shrink-0" />
                                  <span className="text-emerald-400 font-medium">¡Copiado!</span>
                                </>
                              ) : (
                                <>
                                  <Copy size={11} className="shrink-0 text-[#a3a3a3]" />
                                  <span>Copiar</span>
                                </>
                              )}
                            </button>

                            <button
                              type="button"
                              data-testid={`assistant-copy-context-btn-${m.id}`}
                              onClick={() => handleCopy(getMessageContextText(m), m.id, "context")}
                              className="inline-flex items-center gap-1 rounded bg-[#202020] hover:bg-[#2c2c2c] px-2 py-0.5 text-[10px] font-medium text-[#d4d4d4] hover:text-[#f4f1ec] transition-colors border border-[#333] shadow-sm cursor-pointer"
                              title="Copiar con contexto"
                              aria-label="Copiar con contexto"
                            >
                              {copiedState?.id === m.id && copiedState?.type === "context" ? (
                                <>
                                  <Check size={11} className="text-emerald-400 shrink-0" />
                                  <span className="text-emerald-400 font-medium">¡Copiado con contexto!</span>
                                </>
                              ) : (
                                <>
                                  <FileText size={11} className="shrink-0 text-[#a3a3a3]" />
                                  <span>Con contexto</span>
                                </>
                              )}
                            </button>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}

            {/* Mensaje en curso durante streaming SSE */}
            {isStreaming && (
              <div
                data-testid="assistant-streaming-message"
                className="flex gap-2.5 flex-row"
              >
                <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full border border-[#f5b544]/40 bg-[#f5b544]/15 text-[#f5b544] animate-pulse">
                  <Bot size={13} />
                </div>
                <div className="min-w-0 flex-1 max-w-[95%] sm:max-w-[92%] rounded-xl p-3 text-xs leading-relaxed border border-[#3d321d] bg-[#1a1711] text-[#f4f1ec]">
                  <div className="mb-1 flex items-center justify-between gap-2 text-[10px] text-[#f5b544]">
                    <span className="font-medium flex items-center gap-1.5">
                      <Loader2 size={11} className="animate-spin" />
                      {currentSelectedModel
                        ? `${t.assistant.title} · ${currentSelectedModel.model_label} (${currentSelectedModel.provider_label})`
                        : t.assistant.title}
                    </span>
                    <span className="text-[10px] opacity-75">Generando...</span>
                  </div>

                  <div className="relative">
                    {currentDelta ? (
                      <div className="relative">
                        <MarkdownContent content={currentDelta.replace(/<veyra_proposal>[\s\S]*?<\/veyra_proposal>/g, "").trim()} />
                        <span className="inline-block w-1.5 h-3 ml-0.5 bg-[#f5b544] animate-pulse align-middle" />
                      </div>
                    ) : (
                      <div className="text-[#a3a3a3] italic">
                        Iniciando respuesta del Creative Director...
                        <span className="inline-block w-1.5 h-3 ml-1 bg-[#f5b544] animate-pulse align-middle" />
                      </div>
                    )}
                  </div>

                  {/* Propuestas en tiempo real capturadas */}
                  {currentProposals.map((p) => (
                    <Proposal key={p.id} proposal={p} projectId={project.id} data={data} />
                  ))}
                </div>
              </div>
            )}

            <div ref={messagesEndRef} />
          </div>
        )}
      </div>

      {/* 4. Barra de acciones rápidas compacta (disponible si ya hay mensajes) */}
      {thread.messages.length > 0 && !isStreaming && (
        <div className="shrink-0 border-t border-[#242424] bg-[#111] px-3 py-1.5 overflow-x-auto no-scrollbar flex items-center gap-1.5">
          <span className="shrink-0 text-[10px] uppercase font-semibold tracking-wider text-[#666] mr-1">
            {t.assistant.quickActionsTitle}:
          </span>
          {QUICK_ACTIONS.map(({ key, label, prompt }) => (
            <button
              key={key}
              type="button"
              data-testid={`assistant-chip-${key}`}
              onClick={() => handleQuickAction(key, prompt())}
              className="shrink-0 rounded-md border border-[#2b2b2b] bg-[#181818] px-2 py-1 text-[11px] text-[#a3a3a3] hover:border-[#f5b544]/50 hover:bg-[#201c13] hover:text-[#f4f1ec] transition"
            >
              {label()}
            </button>
          ))}
        </div>
      )}

      {/* 5. Área de entrada multilínea, botón de envío y botón "Detener respuesta" */}
      <div className="shrink-0 border-t border-[#303030] bg-[#161616] p-3 space-y-2">
        {streamingError && (
          <div
            data-testid="assistant-stream-error-banner"
            className="rounded-md border border-[#e5484d]/30 bg-[#2b1416] p-2 text-xs text-[#ffb4b7] flex items-center justify-between"
          >
            <span>⚠️ {typeof streamingError === "string" ? streamingError : (streamingError?.msg || streamingError?.message || "Error en el servicio de inferencia.")}</span>
            <button
              type="button"
              onClick={() => setStreamingError(null)}
              className="text-[10px] text-[#ff8b8f] underline ml-2"
            >
              Cerrar
            </button>
          </div>
        )}

        {/* Indicación clara al pulsar Crear guion */}
        {isScriptMode && (
          <div
            data-testid="assistant-script-mode-banner"
            className="flex items-center justify-between gap-2 rounded-md border border-[#f5b544]/30 bg-[#f5b544]/10 px-2.5 py-1 text-xs text-[#f5b544]"
          >
            <div className="flex items-center gap-1.5 truncate">
              <FileText size={12} className="shrink-0" />
              <span className="font-semibold">
                {typeof t.assistant?.quickActions?.script?.label === "function"
                  ? t.assistant.quickActions.script.label()
                  : t.assistant?.quickActions?.script?.label || "Crear guion"}:
              </span>
              <span className="truncate text-[#f4f1ec]">
                {t.assistant?.scriptDraftInstruction || "¿Qué quieres contar? Puedes describir tu idea o pedirme que te proponga algunas"}
              </span>
            </div>
            <button
              type="button"
              data-testid="assistant-script-cancel-btn"
              onClick={() => setIsScriptMode(false)}
              className="text-[10px] text-[#f5b544]/70 hover:text-[#f4f1ec]"
              title={t.assistant?.scriptCancel || "Cancelar"}
            >
              <X size={12} />
            </button>
          </div>
        )}

        {/* Aviso cuando no hay toma seleccionada al pulsar Preparar prompt */}
        {noSceneNotice && (
          <div
            data-testid="assistant-no-scene-notice"
            className="flex items-center justify-between gap-2 rounded-md border border-amber-500/40 bg-[#241a08] p-2 text-xs text-amber-300"
          >
            <div className="flex items-center gap-1.5">
              <Info size={13} className="shrink-0 text-amber-400" />
              <span>{noSceneNotice}</span>
            </div>
            <button
              type="button"
              data-testid="assistant-dismiss-no-scene-notice"
              onClick={() => setNoSceneNotice(null)}
              className="text-[10px] text-amber-300/70 hover:text-white"
              title="Cerrar aviso"
            >
              <X size={12} />
            </button>
          </div>
        )}

        {/* Advertencia cuando la selección de toma cambia respecto al borrador activo */}
        {isSceneMismatch && (
          <div
            data-testid="assistant-draft-scene-mismatch-warning"
            className="flex flex-col gap-1.5 rounded-md border border-[#e5484d]/40 bg-[#2b1416] p-2 text-xs text-[#ffb4b7]"
          >
            <div className="flex items-center gap-1.5 font-medium text-[#fca5a5]">
              <AlertTriangle size={13} className="shrink-0 text-[#ef4444]" />
              <span>{t.assistant?.draftSceneMismatchTitle || "La toma seleccionada ha cambiado."}</span>
            </div>
            <p className="text-[11px] leading-relaxed text-[#ffb4b7]/90">
              {typeof t.assistant?.draftSceneMismatchDesc === "function"
                ? t.assistant.draftSceneMismatchDesc(draftSceneContext.sceneTitle)
                : `El borrador actual está vinculado a «${draftSceneContext.sceneTitle}». Para evitar enviar datos de otra toma, actualiza el borrador antes de enviar.`}
            </p>
            <div className="flex items-center gap-2 mt-0.5">
              <button
                type="button"
                data-testid="assistant-update-draft-btn"
                onClick={handleUpdateDraft}
                className="inline-flex items-center gap-1 rounded border border-[#ef4444]/60 bg-[#3a181b] px-2 py-0.5 text-[11px] font-semibold text-[#fca5a5] hover:bg-[#481c20]"
              >
                <RotateCcw size={11} />
                <span>{t.assistant?.updateDraft || "Actualizar borrador"}</span>
              </button>
              <button
                type="button"
                data-testid="assistant-draft-unlink-mismatch-btn"
                onClick={() => setDraftSceneContext(null)}
                className="text-[10px] text-[#ffb4b7]/70 hover:underline"
              >
                {t.assistant?.unlinkDraft || "Desvincular"}
              </button>
            </div>
          </div>
        )}

        {/* Indicador claro cuando se está trabajando sobre una toma vinculada */}
        {!isSceneMismatch && draftSceneContext && (
          <div
            data-testid="assistant-working-on-scene"
            className="flex items-center justify-between gap-2 rounded-md border border-[#f5b544]/30 bg-[#f5b544]/10 px-2.5 py-1 text-xs text-[#f5b544]"
          >
            <div className="flex items-center gap-1.5 truncate">
              <Wand2 size={12} className="shrink-0" />
              <span className="font-medium">Trabajando sobre:</span>
              <span className="truncate font-semibold text-[#f4f1ec]">{draftSceneContext.sceneTitle}</span>
              {draftSceneContext.mode === "continuity" && (
                <span className="rounded bg-[#f5b544]/20 px-1 py-0.2 text-[10px] text-[#f5b544]">
                  · Corrección de continuidad
                </span>
              )}
            </div>
            <button
              type="button"
              data-testid="assistant-draft-unlink-btn"
              onClick={() => setDraftSceneContext(null)}
              className="text-[10px] text-[#f5b544]/70 hover:text-[#f4f1ec]"
              title="Desvincular borrador"
            >
              {t.assistant?.unlinkDraft || "Desvincular"}
            </button>
          </div>
        )}

        <div className="relative rounded-lg border border-[#303030] bg-[#0f0f0f] focus-within:border-[#f5b544] transition-colors">
          <textarea
            ref={textareaRef}
            data-testid="assistant-input"
            rows={3}
            value={text}
            readOnly={isStreaming}
            onChange={(e) => {
              const val = e.target.value;
              setText(val);
              if (!val.trim() && draftSceneContext?.mode !== "improve" && draftSceneContext?.mode !== "continuity") {
                setDraftSceneContext(null);
              }
            }}
            onKeyDown={handleKeyDown}
            placeholder={
              isStreaming
                ? "Generando respuesta..."
                : draftSceneContext?.mode === "improve"
                ? (t.assistant?.improveInputPh || "¿Qué deseas cambiar o mejorar en esta toma? (ej. Más entorno, conservar estilo…)")
                : draftSceneContext?.mode === "continuity"
                ? "Detalla o ajusta la corrección de continuidad deseada para esta toma..."
                : isScriptMode
                ? (t.assistant?.scriptInputPh || "¿Qué quieres contar? Puedes describir tu idea o pedirme que te proponga algunas")
                : t.assistant.inputPh
            }
            className={`w-full resize-none bg-transparent p-3 text-xs leading-relaxed text-[#f4f1ec] outline-none placeholder:text-[#666] ${
              isStreaming ? "cursor-default opacity-80" : ""
            }`}
          />

          <div className="flex items-center justify-between border-t border-[#222] px-2.5 py-1.5">
            <span className="text-[10px] text-[#6b6b6b]">
              {isStreaming ? "Inferencia SSE en curso..." : t.assistant.newLineHint}
            </span>

            <div className="flex items-center gap-2">
              {!isStreaming && text.trim() && (
                <button
                  type="button"
                  onClick={() => {
                    setText("");
                    setDraftSceneContext(null);
                    setIsScriptMode(false);
                  }}
                  className="text-[10px] text-[#888] hover:text-[#f4f1ec] px-1"
                  title="Limpiar"
                >
                  <RotateCcw size={11} />
                </button>
              )}

              {!isStreaming && (text.includes("PLAN DE PRODUCCIÓN") || text.includes("Directivas obligatorias")) && (
                <button
                  type="button"
                  data-testid="assistant-view-plan-request-btn"
                  onClick={() => setIsPlanRequestModalOpen(true)}
                  className="inline-flex h-7 items-center gap-1.5 rounded-md bg-indigo-950/50 hover:bg-indigo-900/60 border border-indigo-700/50 px-2.5 text-xs font-medium text-indigo-300 hover:text-indigo-200 transition-colors"
                  title="Ver y revisar la solicitud completa en ventana independiente"
                >
                  <Eye size={12} />
                  <span>Ver solicitud</span>
                </button>
              )}

              {isStreaming ? (
                /* Botón "Detener respuesta" con guarda anti-rebote */
                <button
                  type="button"
                  data-testid="assistant-stop-btn"
                  tabIndex={-1}
                  disabled={!canStop}
                  onClick={handleStop}
                  className={`inline-flex h-7 items-center gap-1.5 rounded-md bg-[#e5484d] px-3 text-xs font-semibold text-white transition ${
                    canStop
                      ? "hover:bg-[#f2555a] active:scale-[0.98] cursor-pointer"
                      : "opacity-60 cursor-not-allowed"
                  }`}
                  title={canStop ? "Detener respuesta" : "Iniciando generación..."}
                  aria-label="Detener respuesta"
                >
                  <Square size={11} fill="currentColor" />
                  <span>Detener</span>
                </button>
              ) : (
                /* Botón Enviar */
                <button
                  type="button"
                  data-testid="assistant-send"
                  disabled={!text.trim() || models.length === 0 || isSceneMismatch}
                  onClick={send}
                  className="inline-flex h-7 items-center gap-1.5 rounded-md bg-[#f5b544] px-3 text-xs font-semibold text-[#1a1206] transition hover:bg-[#ffc45a] active:scale-[0.98] disabled:opacity-40 disabled:pointer-events-none"
                  aria-label={t.assistant.send}
                >
                  <Send size={12} />
                  <span>{t.assistant.send}</span>
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Modal para ver y editar la solicitud de planificación en ventana amplia */}
      <PlanRequestModal
        open={isPlanRequestModalOpen}
        onClose={() => setIsPlanRequestModalOpen(false)}
        requestText={text}
        onSaveAndApply={(newText) => setText(newText)}
        onSend={(newText) => {
          setText(newText);
          setTimeout(() => {
            send();
          }, 50);
        }}
      />

      {/* Modal para revisar el plan activo si se hace clic desde el banner superior */}
      {project?.activePlan && (
        <ProductionPlanModal
          open={activePlanModalOpen}
          onClose={() => setActivePlanModalOpen(false)}
          plan={project.activePlan}
          project={project}
          isActive={true}
          onActivatePlan={handleActivatePlan}
          onRequestChanges={handleRequestChangesOnPlan}
        />
      )}
    </div>
  );
};

const AssistantPanel = (props) => {
  const { isOwner } = useAuth();
  if (!isOwner) return null;
  return <AssistantPanelInner {...props} />;
};

export default AssistantPanel;
